<template>
<div>
    
  <div class="flex flex-row items-top justify-between">
    <h1 class="text-lg font-semibold mb-3">Dashboard</h1>
  </div>

  <hr class="border-b border-gray-100 mb-3">    

  <!-- Subscription Status - Shows only when no active subscription -->
  <subscription-status ref="subscriptionStatus"></subscription-status>

  <!-- Share Modal -->
  <share-modal
    v-if="showShareModal"
    :visible="showShareModal"
    :scan-id="shareScanId"
    :scan-type="shareScanType"
    @close="closeShareModal"
    @copied="onShareCopied"
  />

  <!-- Dashboard Content - Only show when user has active subscription -->
  <div v-if="hasActiveSubscription">
    <!-- API Key Status Message -->
    <div v-if="!isGoogleMapsValid" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-medium text-yellow-800">
          Google Maps API Key Required
        </h3>
        <div class="mt-2 text-sm text-yellow-700">
          <p v-if="!isGoogleMapsConfigured">
            Please configure your Google Maps API key to use Quick Scan and Grid features.
          </p>
          <p v-else>
            Your Google Maps API key is invalid or not working. Please check your API key settings.
          </p>
          <router-link to="/user-settings" class="font-medium underline text-yellow-600 hover:text-yellow-500 mt-2 inline-block">
            Go to Settings →
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <div class="">
    <!-- Header Section with Background -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-info">
          <h1 class="page-title">🎯 Local GRID Rank Tracker</h1>
          <p class="page-description">Monitor your local search performance across multiple grid locations and keywords</p>
        </div>
        <div class="header-actions">
          <button class="add-grid-btn" @click="openQuickScanModal">
            <span class="btn-icon">⚡</span>
            Quick Scan
          </button>
          <button class="add-grid-btn" @click="openAddGridModal">
            <span class="btn-icon">➕</span>
            Add New Grid
          </button>
        </div>
      </div>
      
      <!-- Overview Widgets -->
      <div class="overview-widgets">
        <template v-if="isLoadingStats">
          <div class="widget-card" v-for="i in 4" :key="'skeleton-' + i">
            <div class="skeleton-loader">
              <div class="skeleton-header"></div>
              <div class="skeleton-value"></div>
              <div class="skeleton-subtitle"></div>
            </div>
          </div>
        </template>
        
        <template v-else>
          <div class="widget-card">
            <div class="widget-header">
              <div class="widget-title">Total Grids</div>
              <div class="widget-change positive" v-if="dashboardStats.totalGridsChange">
                +{{ dashboardStats.totalGridsChange }}
              </div>
            </div>
            <div class="widget-value">{{ dashboardStats.totalGrids }}</div>
            <div class="widget-subtitle">Active tracking grids</div>
            <button class="widget-view-btn" @click="openGridsListModal" title="View All Grids">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </button>
          </div>

          <div class="widget-card">
            <div class="widget-header">
              <div class="widget-title">Average Rank</div>
              <div class="widget-change" 
                   :class="dashboardStats.averageRankTrend === 'up' ? 'positive' : dashboardStats.averageRankTrend === 'down' ? 'negative' : ''"
                   v-if="dashboardStats.averageRankChange">
                {{ dashboardStats.averageRankChange > 0 ? '+' : '' }}{{ dashboardStats.averageRankChange }}%
              </div>
            </div>
            <div class="widget-value">{{ dashboardStats.averageRank }}</div>
            <div class="widget-subtitle">Across all keywords</div>
          </div>

          <div class="widget-card">
            <div class="widget-header">
              <div class="widget-title">Top 3 Positions</div>
              <div class="widget-change" 
                   :class="dashboardStats.top3Trend === 'up' ? 'positive' : dashboardStats.top3Trend === 'down' ? 'negative' : ''"
                   v-if="dashboardStats.top3Change">
                {{ dashboardStats.top3Change > 0 ? '+' : '' }}{{ dashboardStats.top3Change }}%
              </div>
            </div>
            <div class="widget-value">{{ dashboardStats.top3Percentage }}%</div>
            <div class="widget-subtitle">Keywords in top 3</div>
          </div>

          <div class="widget-card">
            <div class="widget-header">
              <div class="widget-title">Grid Coverage</div>
              <div class="widget-change" 
                   :class="dashboardStats.gridCoverageTrend === 'up' ? 'positive' : dashboardStats.gridCoverageTrend === 'down' ? 'negative' : ''"
                   v-if="dashboardStats.gridCoverageChange">
                {{ dashboardStats.gridCoverageChange > 0 ? '+' : '' }}{{ dashboardStats.gridCoverageChange }}%
              </div>
            </div>
            <div class="widget-value">{{ dashboardStats.gridCoverage }}%</div>
            <div class="widget-subtitle">Average visibility</div>
          </div>
        </template>
      </div>
    </div>

    <div class="listing-table-container">
      <!-- Section Title -->
      <div class="table-section-header">
        <h2 class="table-section-title">📊 Scan History</h2>
        <p class="table-section-description">View and manage your recent grid scanning activity</p>
      </div>

      <!-- Header Controls -->
      <div class="header-controls">
        <div class="search-filter-group">
          <div class="search-bar">
            <div class="search-icon">
              <svg viewBox="0 0 24 24" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
            <input v-model="searchQuery" type="text" placeholder="Search">
          </div>
          <div class="filter-button">
            <svg viewBox="0 0 24 24" stroke-width="2">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
            </svg>
          </div>
        </div>
        <div class="actions-dropdown-container">
          <div class="actions-dropdown" @click="toggleActionsDropdown">
            <span>Actions</span>
            <div class="arrow-down">
              <svg viewBox="0 0 24 24" stroke-width="2">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </div>
          </div>
          <!-- Dropdown Menu -->
          <div v-if="showActionsDropdown" class="actions-menu">
            <button 
              class="action-menu-item delete" 
              @click="deleteSelectedScans"
              :disabled="selectedRows.length === 0"
              :class="{ disabled: selectedRows.length === 0 }"
            >
              <span style="margin-right: 8px;">🗑️</span>
              Delete Selected ({{ selectedRows.length }})
            </button>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="table-container">
        <!-- Add loading state -->
        <div v-if="isLoadingScans" class="loading-state" style="text-align: center; padding: 40px;">
          <p>Loading scan history...</p>
        </div>
        
        <!-- Add error state -->
        <div v-else-if="scanError" class="error-state" style="text-align: center; padding: 40px; color: #ef4444;">
          <p>{{ scanError }}</p>
          <button @click="fetchQuickScans()" class="action-btn primary" style="margin-top: 10px;">
            Retry
          </button>
        </div>
        
        <!-- Keep your existing table header -->
        <div v-else class="table-header">
          <div class="table-cell checkbox-cell">
            <div 
              class="checkbox" 
              :class="{ checked: isAllSelected }"
              @click="toggleSelectAll"
            >
              <span v-if="isAllSelected">✓</span>
            </div>
          </div>
          <div class="table-cell scan-type-cell">Scan Type</div>
          <div class="table-cell location-cell">Business Name</div>
          <div class="table-cell search-query-cell">Keywords</div>
          <div class="table-cell search-settings-cell">Search Settings</div>
          <div class="table-cell last-scan-cell">Last Scan</div>
          <div class="table-cell action-cell">Action</div>
        </div>

        <!-- Update table rows section -->
        <div v-if="!isLoadingScans && !scanError">
          <div 
            v-for="(row, index) in paginatedData" 
            :key="index" 
            class="table-row"
          >
            <!-- Keep your existing table row content - it will automatically show the API data -->
            <div class="table-cell checkbox-cell">
              <div 
                class="checkbox" 
                :class="{ checked: isRowSelected(row.id) }"
                @click="toggleRowSelect(row.id)"
              >
                <span v-if="isRowSelected(row.id)">✓</span>
              </div>
            </div>
            <div class="table-cell scan-type-cell">
              <span v-if="row.scan_type === 'grid_scan'" class="scan-type-label grid">
                <span class="type-icon">📅</span> Grid Scan
              </span>
              <span v-else class="scan-type-label quick">
                <span class="type-icon">⚡</span> Quick Scan
              </span>
            </div>
            <div class="table-cell location-cell">
              {{ row.businessName }}
            </div>
            <div class="table-cell search-query-cell">
              <div class="keywords-container">
                <span 
                  v-for="(keyword, index) in getKeywordArray(row.keywords)" 
                  :key="index" 
                  class="keyword-badge"
                >
                  {{ keyword }}
                </span>
              </div>
            </div>
            <div class="table-cell search-settings-cell">
              <div class="grid-icon">
                <svg viewBox="0 0 24 24">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <span>{{ row.gridSize }}</span>
              <div class="miles-icon">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="8,12 12,16 16,12"/>
                </svg>
              </div>
              <span>{{ row.radius }}</span>
            </div>
            <div class="table-cell last-scan-cell">{{ row.lastScan }}</div>
            <div class="table-cell action-cell">
              <!-- Keep your existing action buttons -->
              <button class="action-button" @click="scanAgain(row)">
                <svg viewBox="0 0 24 24">
                  <path d="M1 4v6h6M23 20v-6h-6"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Scan Again
              </button>
              <button class="action-button" @click="viewDetails(row)">
                <svg viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
                View Details
              </button>
              <button class="action-button" @click="shareScan(row)">
                <svg viewBox="0 0 24 24">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share
              </button>
            </div>
          </div>
        </div>

        <!-- Show message when no data -->
        <div v-if="!isLoadingScans && !scanError && tableData.length === 0" class="no-data-state" style="text-align: center; padding: 40px;">
          <p>No scan history found.</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination">
        <div class="pagination-left">
          <span>Show</span>
          <div class="pagination-dropdown">{{ itemsPerPage }}</div>
          <span>Entries</span>
        </div>
        <div class="pagination-right">
          <span>{{ paginationInfo }}</span>
          <div 
            class="pagination-button" 
            :class="{ disabled: currentPage === 1 }"
            @click="previousPage"
          >
            <svg viewBox="0 0 24 24">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </div>
          <div 
            class="pagination-button" 
            :class="{ disabled: currentPage >= totalPages }"
            @click="nextPage"
          >
            <svg viewBox="0 0 24 24">
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Scan Modal -->
    <div v-if="showQuickScanModal" class="modal" @click="closeModalOnBackdrop($event, 'quickScan')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">⚡ Quick Scan</h3>
          <button class="close-modal" @click="closeQuickScanModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="quick-scan-description">
            <p>Run a one-time scan that will appear in your scan history. Quick scans are not saved as permanent grids.</p>
          </div>
          
          <div class="form-group">
            <label>Keywords:</label>
            <div class="keywords-input-container">
              <div class="keywords-tag-container">
                <div class="keywords-tag-wrapper">
                  <div 
                    v-for="(keyword, index) in quickScan.keywords" 
                    :key="index" 
                    class="keyword-tag-item"
                  >
                    <span 
                      class="keyword-tag-text" 
                      contenteditable="true"
                      @blur="editKeyword(index, $event)"
                      @keypress.enter.prevent="$event.target.blur()"
                    >{{ keyword }}</span>
                    <button class="keyword-tag-delete" @click="removeKeyword(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input 
                    v-model="quickScan.keywordInput"
                    @keypress.enter.prevent="addKeyword"
                    @input="handleKeywordInput"
                    type="text" 
                    class="keyword-tag-input" 
                    placeholder="Type a keyword and press Enter..."
                  >
                </div>
              </div>
              <div v-if="keywordLimitError" class="keyword-error-message">  ⚠️ Quick Scan accepts only one keyword.</div>
              <div class="input-hint">Press Enter to add keywords • Click to edit • Click × to delete</div>
            </div>
          </div>
          
          <div class="form-group">
            <label>Search for Your Business:</label>
            
            <!-- Search Provider Toggle -->
            <div class="search-provider-toggle" style="display: flex; align-items: center; margin-bottom: 12px; margin-top: 8px;">
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input 
                  type="checkbox" 
                  v-model="quickScan.showValueSerpSearch"
                  @change="toggleSearchProvider('quickScan')"
                  style="display: none;"
                >
                <div class="toggle-switch" :class="{ 'active': quickScan.showValueSerpSearch }" 
                     style="position: relative; width: 48px; height: 24px; border-radius: 12px; transition: all 0.3s;"
                     :style="{ backgroundColor: quickScan.showValueSerpSearch ? '#4285f4' : '#ccc' }">
                  <div class="toggle-slider" 
                       style="position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background-color: white; border-radius: 50%; transition: transform 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" 
                       :style="{ transform: quickScan.showValueSerpSearch ? 'translateX(24px)' : 'translateX(0)' }"></div>
                </div>
                <span style="margin-left: 10px; font-size: 14px; color: #333;">Use Value SERP Search for SAB</span>
              </label>
            </div>
            <div v-if="quickScan.showValueSerpSearch" class="input-hint" style="margin-bottom: 10px; color: #666; font-size: 12px;">
              Can't find your business in Google? Value SERP provides extended search results
            </div>
            
            <!-- Google Places Search (Default) -->
            <div v-if="!quickScan.showValueSerpSearch">
              <input 
                ref="quickScanBusinessSearch"
                v-model="quickScan.businessSearch"
                type="text" 
                placeholder="Search for your business name and location..." 
                style="width: 100%;"
              >
              <div class="input-hint">Start typing your business name to search using Google Places</div>
            </div>
            
            <!-- Value SERP Search with Autocomplete -->
            <div v-if="quickScan.showValueSerpSearch" style="position: relative;">
              <input 
                v-model="quickScan.valueSerpKeyword"
                @input="onValueSerpInput('quickScan')"
                @focus="quickScan.showValueSerpDropdown = true"
                @blur="hideValueSerpDropdown('quickScan')"
                class="form-control"
                placeholder="Type business name and location..."
                style="width: 100%; padding: 10px 12px; font-size: 14px; border: 1px solid #ddd; border-radius: 4px;"
                autocomplete="off"
              >
              
              <!-- Loading indicator inside input -->
              <div v-if="quickScan.valueSerpSearchLoading" style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%);">
                <i class="fa fa-spinner fa-spin" style="color: #999;"></i>
              </div>
              
              <!-- Loading state in dropdown -->
              <div v-if="quickScan.showValueSerpDropdown && quickScan.valueSerpSearchLoading && quickScan.valueSerpKeyword.length >= 3" 
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); padding: 20px; text-align: center; z-index: 1000;">
                <i class="fa fa-spinner fa-spin" style="color: #999; font-size: 20px;"></i>
                <div style="margin-top: 10px; color: #666; font-size: 13px;">Searching businesses...</div>
              </div>
              
              <!-- Autocomplete Dropdown -->
              <div v-if="quickScan.showValueSerpDropdown && !quickScan.valueSerpSearchLoading && quickScan.valueSerpSearchResults.length > 0" 
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-height: 300px; overflow-y: auto; z-index: 1000;">
                <div 
                  v-for="(result, index) in quickScan.valueSerpSearchResults" 
                  :key="result.data_cid || index"
                  @mousedown.prevent="selectValueSerpBusiness(result, 'quickScan')"
                  class="autocomplete-item"
                  style="padding: 10px 12px; cursor: pointer; border-bottom: 1px solid #f0f0f0; transition: background-color 0.2s;"
                  :style="{ backgroundColor: quickScan.hoveredIndex === index ? '#f5f5f5' : 'white' }"
                  @mouseenter="quickScan.hoveredIndex = index"
                  @mouseleave="quickScan.hoveredIndex = -1"
                >
                  <div style="font-size: 14px; color: #333; font-weight: 500; line-height: 1.4;">{{ result.title }}</div>
                  <div style="font-size: 12px; color: #666; margin-top: 2px; line-height: 1.3;">{{ result.address }}</div>
                </div>
              </div>
              
              <!-- No results message -->
              <div v-if="quickScan.showValueSerpDropdown && quickScan.valueSerpKeyword.length > 2 && !quickScan.valueSerpSearchLoading && quickScan.valueSerpSearchResults.length === 0"
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; padding: 12px; text-align: center; color: #666; font-size: 13px;">
                No businesses found. Try a different search term.
              </div>
              
              <div class="input-hint mt-1">Search using Value SERP API • Results appear as you type</div>
            </div>
          </div>
          
          <div v-if="quickScan.selectedBusiness" class="form-row">
            <div class="form-group" style="position: relative;">
              <label>Selected Business:</label>
              <input 
                v-model="quickScan.selectedBusiness.name"
                type="text" 
                placeholder="Business name will appear here" 
                readonly 
                style="background-color: #f9fafb;"
              >
              <i v-if="quickScan.valueSerpDetailLoading" class="fa fa-spinner fa-spin" style="position: absolute; right: 10px; top: 38px; color: #999;"></i>
            </div>
            <div class="form-group" style="position: relative;">
              <label>Address:</label>
              <input 
                v-model="quickScan.selectedBusiness.address"
                type="text" 
                placeholder="Address will appear here" 
                readonly 
                style="background-color: #f9fafb;"
              >
              <i v-if="quickScan.valueSerpDetailLoading" class="fa fa-spinner fa-spin" style="position: absolute; right: 10px; top: 38px; color: #999;"></i>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>Grid Size:</label>
              <select v-model="quickScan.gridSize" class="form-select" @change="updateQuickScanPreview">
                <option value="3x3">3 x 3 (9 locations)</option>
                <option value="5x5">5 x 5 (25 locations)</option>
                <option value="7x7">7 x 7 (49 locations)</option>
              </select>
            </div>
            <div class="form-group">
              <label>Radius:</label>
              <select v-model="quickScan.radius" class="form-select" @change="updateQuickScanPreview">
                <option value="0.1">0.1 Miles</option>
                <option value="0.2">0.2 Miles</option>
                <option value="0.5">0.5 Miles</option>
                <option value="1">1 Mile</option>
                <option value="1.5">1.5 Miles</option>
                <option value="2.5">2.5 Miles</option>
                <option value="5">5 Miles</option>
                <option value="10">10 Miles</option>
                <option value="25">25 Miles</option>
                <option value="30">30 Miles</option>
              </select>
            </div>
          </div>
          
          <!-- Quick Scan Grid Preview -->
          <div class="form-group">
            <label>Scan Area Preview:</label>
            <div class="grid-preview-container">
              <div class="grid-preview-map" style="position: relative;">
                <div ref="quickScanGoogleMap" style="width: 100%; height: 300px;"></div>
                <div class="grid-info-badge" style="position: absolute; top: 10px; left: 50%; transform: translateX(-50%); z-index: 100;">
                  <span>{{ quickScan.gridSize }} Grid • {{ quickScan.radius }} Mile Radius</span>
                </div>
              </div>
              <div class="map-hint">Search for your business above • Drag the grid to adjust position</div>
            </div>
          </div>
          
          <div class="form-group">
            <label>Competitors (Optional):</label>
            <div class="competitors-input-container">
              <input 
                ref="quickScanCompetitorSearch"
                v-model="quickScan.competitorSearch"
                type="text" 
                class="competitor-search-input" 
                placeholder="Search for competitor businesses..."
              >
              <div v-if="quickScan.competitors.length > 0" class="competitors-tag-container" style="margin-top: 8px;">
                <div class="competitors-tag-wrapper">
                  <div 
                    v-for="(competitor, index) in quickScan.competitors" 
                    :key="index" 
                    class="competitor-tag-item"
                  >
                    <span class="competitor-tag-text" :title="competitor.address">{{ competitor.name }}</span>
                    <button class="competitor-tag-delete" @click="removeCompetitor(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="input-hint">Search and select competitor businesses • Click × to remove</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn secondary" @click="closeQuickScanModal">Cancel</button>
          <button 
            class="action-btn primary scan-btn" 
            @click="startQuickScan"
            :disabled="quickScanLoading"
            :class="{ 'loading': quickScanLoading }"
          >
            <span v-if="!quickScanLoading">⚡</span>
            <span v-if="quickScanLoading" class="spinner"></span>
            {{ quickScanLoading ? 'Processing...' : 'Start Quick Scan' }}
          </button>
        </div>
      </div>
    </div>
    <!-- Add New Grid Modal -->
    <div v-if="showAddGridModal" class="modal" @click="closeModalOnBackdrop($event, 'addGrid')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ isEditMode ? '✏️ Edit Grid' : '➕ Add New Grid' }}</h3>
          <button class="close-modal" @click="closeAddGridModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Search for Your Business:</label>
            
            <!-- Search Provider Toggle -->
            <div class="search-provider-toggle" style="display: flex; align-items: center; margin-bottom: 12px; margin-top: 8px;">
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input 
                  type="checkbox" 
                  v-model="addGrid.showValueSerpSearch"
                  @change="toggleSearchProvider('addGrid')"
                  style="display: none;"
                >
                <div class="toggle-switch" :class="{ 'active': addGrid.showValueSerpSearch }" 
                     style="position: relative; width: 48px; height: 24px; border-radius: 12px; transition: all 0.3s;"
                     :style="{ backgroundColor: addGrid.showValueSerpSearch ? '#4285f4' : '#ccc' }">
                  <div class="toggle-slider" 
                       style="position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background-color: white; border-radius: 50%; transition: transform 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" 
                       :style="{ transform: addGrid.showValueSerpSearch ? 'translateX(24px)' : 'translateX(0)' }"></div>
                </div>
                <span style="margin-left: 10px; font-size: 14px; color: #333;">Use Value SERP Search for SAB</span>
              </label>
            </div>
            <div v-if="addGrid.showValueSerpSearch" class="input-hint" style="margin-bottom: 10px; color: #666; font-size: 12px;">
              Can't find your business in Google? Value SERP provides extended search results
            </div>
            
            <!-- Google Places Search (Default) -->
            <div v-if="!addGrid.showValueSerpSearch">
              <input 
                ref="addGridBusinessSearch"
                v-model="addGrid.businessSearch"
                type="text" 
                placeholder="Search for your business name and location..." 
                style="width: 100%;"
              >
              <div class="input-hint">Start typing your business name to search using Google Places</div>
            </div>
            
            <!-- Value SERP Search with Autocomplete -->
            <div v-if="addGrid.showValueSerpSearch" style="position: relative;">
              <input 
                v-model="addGrid.valueSerpKeyword"
                @input="onValueSerpInput('addGrid')"
                @focus="addGrid.showValueSerpDropdown = true"
                @blur="hideValueSerpDropdown('addGrid')"
                class="form-control"
                placeholder="Type business name and location..."
                style="width: 100%; padding: 10px 12px; font-size: 14px; border: 1px solid #ddd; border-radius: 4px;"
                autocomplete="off"
              >
              
              <!-- Loading indicator inside input -->
              <div v-if="addGrid.valueSerpSearchLoading" style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%);">
                <i class="fa fa-spinner fa-spin" style="color: #999;"></i>
              </div>
              
              <!-- Loading state in dropdown -->
              <div v-if="addGrid.showValueSerpDropdown && addGrid.valueSerpSearchLoading && addGrid.valueSerpKeyword.length >= 3" 
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); padding: 20px; text-align: center; z-index: 1000;">
                <i class="fa fa-spinner fa-spin" style="color: #999; font-size: 20px;"></i>
                <div style="margin-top: 10px; color: #666; font-size: 13px;">Searching businesses...</div>
              </div>
              
              <!-- Autocomplete Dropdown -->
              <div v-if="addGrid.showValueSerpDropdown && !addGrid.valueSerpSearchLoading && addGrid.valueSerpSearchResults.length > 0" 
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-height: 300px; overflow-y: auto; z-index: 1000;">
                <div 
                  v-for="(result, index) in addGrid.valueSerpSearchResults" 
                  :key="result.data_cid || index"
                  @mousedown.prevent="selectValueSerpBusiness(result, 'addGrid')"
                  class="autocomplete-item"
                  style="padding: 10px 12px; cursor: pointer; border-bottom: 1px solid #f0f0f0; transition: background-color 0.2s;"
                  :style="{ backgroundColor: addGrid.hoveredIndex === index ? '#f5f5f5' : 'white' }"
                  @mouseenter="addGrid.hoveredIndex = index"
                  @mouseleave="addGrid.hoveredIndex = -1"
                >
                  <div style="font-size: 14px; color: #333; font-weight: 500; line-height: 1.4;">{{ result.title }}</div>
                  <div style="font-size: 12px; color: #666; margin-top: 2px; line-height: 1.3;">{{ result.address }}</div>
                </div>
              </div>
              
              <!-- No results message -->
              <div v-if="addGrid.showValueSerpDropdown && addGrid.valueSerpKeyword.length > 2 && !addGrid.valueSerpSearchLoading && addGrid.valueSerpSearchResults.length === 0"
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; padding: 12px; text-align: center; color: #666; font-size: 13px;">
                No businesses found. Try a different search term.
              </div>
              
              <div class="input-hint mt-1">Search using Value SERP API • Results appear as you type</div>
            </div>
          </div>
          
          <div v-if="addGrid.selectedBusiness" class="form-row">
            <div class="form-group" style="position: relative;">
              <label>Selected Business:</label>
              <input 
                v-model="addGrid.selectedBusiness.name"
                type="text" 
                placeholder="Business name will appear here" 
                readonly 
                style="background-color: #f9fafb;"
              >
              <i v-if="addGrid.valueSerpDetailLoading" class="fa fa-spinner fa-spin" style="position: absolute; right: 10px; top: 38px; color: #999;"></i>
            </div>
            <div class="form-group" style="position: relative;">
              <label>Address:</label>
              <input 
                v-model="addGrid.selectedBusiness.address"
                type="text" 
                placeholder="Address will appear here" 
                readonly 
                style="background-color: #f9fafb;"
              >
              <i v-if="addGrid.valueSerpDetailLoading" class="fa fa-spinner fa-spin" style="position: absolute; right: 10px; top: 38px; color: #999;"></i>
            </div>
          </div>
          
          <div class="form-group">
            <label>Keywords:</label>
            <div class="keywords-input-container">
              <div class="keywords-tag-container">
                <div class="keywords-tag-wrapper">
                  <div 
                    v-for="(keyword, index) in addGrid.keywords" 
                    :key="index" 
                    class="keyword-tag-item"
                  >
                    <span 
                      class="keyword-tag-text" 
                      contenteditable="true"
                      @blur="editAddGridKeyword(index, $event)"
                      @keypress.enter.prevent="$event.target.blur()"
                    >{{ keyword }}</span>
                    <button class="keyword-tag-delete" @click="removeAddGridKeyword(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input 
                    v-model="addGrid.keywordInput"
                    @keypress.enter.prevent="addAddGridKeyword"
                    @input="handleAddGridKeywordInput"
                    type="text" 
                    class="keyword-tag-input" 
                    placeholder="Type a keyword and press Enter..."
                  >
                </div>
              </div>
              <div class="input-hint">Press Enter to add keywords • Click to edit • Click × to delete</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Grid Size:</label>
              <select v-model="addGrid.gridSize" class="form-select" @change="updateAddGridPreview">
                <option value="3x3">3 x 3 (9 locations)</option>
                <option value="5x5">5 x 5 (25 locations)</option>
                <option value="7x7">7 x 7 (49 locations)</option>
                <option value="10x10">10 x 10 (100 locations)</option>
              </select>
            </div>
            <div class="form-group">
              <label>Radius:</label>
              <select v-model="addGrid.radius" class="form-select" @change="updateAddGridPreview">
                <option value="0.1">0.1 Miles</option>
                <option value="0.2">0.2 Miles</option>
                <option value="0.5">0.5 Miles</option>
                <option value="1">1 Mile</option>
                <option value="1.5">1.5 Miles</option>
                <option value="2.5">2.5 Miles</option>
                <option value="5">5 Miles</option>
                <option value="10">10 Miles</option>
                <option value="25">25 Miles</option>
                <option value="30">30 Miles</option>
              </select>
            </div>
          </div>
          
          <!-- Grid Preview Map -->
          <div class="form-group">
            <label>Grid Coverage Preview:</label>
            <div class="grid-preview-container">
              <div class="grid-preview-map" style="position: relative;">
                <div ref="addGridGoogleMap" style="width: 100%; height: 300px;"></div>
                <div class="grid-info-badge" style="position: absolute; top: 10px; left: 50%; transform: translateX(-50%); z-index: 100;">
                  <span>{{ addGrid.gridSize }} Grid • {{ addGrid.radius }} Mile Radius</span>
                </div>
              </div>
              <div class="map-hint">Search for your business above • Drag the grid to adjust position</div>
            </div>
          </div>

          <div class="form-group">
            <label>Competitors (Optional):</label>
            <div class="competitors-input-container">
              <input 
                ref="addGridCompetitorSearch"
                v-model="addGrid.competitorSearch"
                type="text" 
                class="competitor-search-input" 
                placeholder="Search for competitor businesses..."
              >
              <div v-if="addGrid.competitors.length > 0" class="competitors-tag-container" style="margin-top: 8px;">
                <div class="competitors-tag-wrapper">
                  <div 
                    v-for="(competitor, index) in addGrid.competitors" 
                    :key="index" 
                    class="competitor-tag-item"
                  >
                    <span class="competitor-tag-text" :title="competitor.address">{{ competitor.name }}</span>
                    <button class="competitor-tag-delete" @click="removeAddGridCompetitor(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="input-hint">Search and select competitor businesses • Click × to remove</div>
            </div>
          </div>

          <div class="form-group">
            <label>Grid Name (Optional):</label>
            <input v-model="addGrid.gridName" type="text" placeholder="e.g., Downtown Area Tracking">
          </div>
          
          <!-- Scheduling Options -->
          <div class="scheduling-section">
            <h4 class="section-title">📅 Scheduling Options</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Scan Frequency:</label>
                <select v-model="addGrid.scanFrequency" class="form-select">
                  <option value="manual">Manual Only</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div class="form-group">
                <label>Preferred Time:</label>
                <select v-model="addGrid.scanTime" class="form-select">
                  <option value="morning">Morning (9:00 AM)</option>
                  <option value="afternoon">Afternoon (2:00 PM)</option>
                  <option value="evening">Evening (6:00 PM)</option>
                  <option value="night">Night (11:00 PM)</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label>Email Recipients:</label>
              <div class="email-tags-container">
                <div class="email-tags-wrapper">
                  <div 
                    v-for="(email, index) in addGrid.emailRecipients" 
                    :key="index" 
                    class="email-tag-item"
                    :class="{ invalid: !isValidEmail(email) }"
                  >
                    <span class="email-tag-text" :title="email">{{ email }}</span>
                    <button class="email-tag-delete" @click="removeEmailRecipient(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input 
                    v-model="addGrid.emailInput"
                    @keypress.enter.prevent="addEmailRecipient"
                    @input="handleEmailInput"
                    type="email" 
                    class="email-tag-input" 
                    placeholder="Enter email address and press Enter..."
                  >
                </div>
              </div>
              <div class="input-hint">Press Enter to add email addresses • Click × to delete</div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Report Format:</label>
                <select v-model="addGrid.reportFormat" class="form-select">
                  <option value="pdf">PDF Report</option>
                  <option value="csv">CSV Data</option>
                  <option value="xlsx">Excel Spreadsheet</option>
                  <option value="both">Both PDF & CSV</option>
                </select>
              </div>
              <div class="form-group">
                <label>Report Name:</label>
                <input v-model="addGrid.reportName" type="text" placeholder="Weekly Ranking Report">
              </div>
            </div>
            <div class="form-group">
              <label>Email Notifications:</label>
              <div class="notification-options">
                <label class="checkbox-label">
                  <input v-model="addGrid.emailOnComplete" type="checkbox">
                  <span class="checkmark"></span>
                  Send email when scan completes
                </label>
                <!-- <label class="checkbox-label">
                  <input v-model="addGrid.emailOnChanges" type="checkbox">
                  <span class="checkmark"></span>
                  Send email only on ranking changes
                </label>
                <label class="checkbox-label">
                  <input v-model="addGrid.weeklyReports" type="checkbox">
                  <span class="checkmark"></span>
                  Send weekly summary reports
                </label> -->
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn secondary" @click="closeAddGridModal">Cancel</button>
          <button class="action-btn primary" @click="saveNewGrid">{{ isEditMode ? 'Update Grid' : 'Save Grid' }}</button>
        </div>
      </div>
    </div>

    <!-- Grids List Modal -->
    <div v-if="showGridsListModal" class="modal" @click="closeModalOnBackdrop($event, 'gridsList')">
      <div class="modal-content grids-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">📊 All Tracking Grids</h3>
          <button class="close-modal" @click="closeGridsListModal">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="isLoadingGrids" class="loading-container">
            <v-skeleton-loader type="article"></v-skeleton-loader>
            <v-skeleton-loader type="article" class="mt-4"></v-skeleton-loader>
            <v-skeleton-loader type="article" class="mt-4"></v-skeleton-loader>
          </div>
          
          <!-- Error State -->
          <div v-else-if="gridsError" class="error-container" style="text-align: center; padding: 40px;">
            <p style="color: #ef4444; margin-bottom: 16px;">{{ gridsError }}</p>
            <button @click="fetchUserGridScans()" class="action-btn primary">
              Retry
            </button>
          </div>
          
          <!-- Empty State -->
          <div v-else-if="!gridScans || gridScans.length === 0" class="empty-state" style="text-align: center; padding: 60px 20px;">
            <div style="font-size: 48px; margin-bottom: 16px;">📊</div>
            <h3 style="margin-bottom: 8px;">No Grid Added</h3>
            <p style="color: #6b7280; margin-bottom: 24px;">Start tracking your local rankings by creating your first grid scan.</p>
            <div class="d-flex justify-content-center mt-5">
              <button @click="openAddGridFromList()" class="action-btn primary">
              <span style="margin-right: 8px;">➕</span>
              Add Your First Grid
            </button>
            </div>
          </div>
          
          <!-- Grid Cards -->
          <div v-else class="grids-container">
            <div 
              v-for="grid in gridScans" 
              :key="grid.id" 
              class="grid-card"
              @click="viewGridDetails(grid.id)"
            >
              <div class="grid-card-header">
                <div>
                  <div class="grid-card-title">{{ grid.gridName || grid.grid_name }}</div>
                  <div class="grid-card-subtitle">
                    Last scan: {{ grid.last_run_at ? formatLastRun(grid.last_run_at) : 'Never' }}
                  </div>
                </div>
                <div class="grid-status-badge" :class="(grid.status || 'pending').toLowerCase()">
                  {{ grid.status || 'Pending' }}
                </div>
              </div>
              
              <div class="grid-settings">
                <div class="grid-setting-item">
                  <div class="grid-setting-label">Grid Size</div>
                  <div class="grid-setting-value">{{ grid.gridSize || grid.grid_size }} ({{ getLocationCount(grid.gridSize || grid.grid_size) }} locations)</div>
                </div>
                <div class="grid-setting-item">
                  <div class="grid-setting-label">Radius</div>
                  <div class="grid-setting-value">{{ grid.radius }} Mile{{ grid.radius !== '1' ? 's' : '' }}</div>
                </div>
                <div class="grid-setting-item">
                  <div class="grid-setting-label">Frequency</div>
                  <div class="grid-setting-value">{{ capitalizeFirst(grid.frequency || grid.scan_frequency) }} - {{ formatScanTime(grid.scanTime || grid.scan_time) }}</div>
                </div>
                <div class="grid-setting-item">
                  <div class="grid-setting-label">Report Format</div>
                  <div class="grid-setting-value">PDF</div>
                </div>
              </div>
              
              <div class="grid-keywords">
                <div class="grid-keywords-label">Keywords ({{ getKeywordsArray(grid.keywords).length }})</div>
                <div class="grid-keywords-list">
                  <span v-for="keyword in getKeywordsArray(grid.keywords)" :key="keyword" class="keyword-tag">{{ keyword }}</span>
                </div>
              </div>
              
              <div class="grid-keywords">
                <div class="grid-keywords-label">Next Scan</div>
                <div style="font-size: 12px; color: #757B89;">
                  {{ grid.nextRun || grid.next_run_at ? formatNextRun(grid.nextRun || grid.next_run_at) : 'Not scheduled' }}
                </div>
              </div>
              
              <div class="grid-keywords" v-if="grid.notification_email">
                <div class="grid-keywords-label">Email Recipients</div>
                <div style="font-size: 12px; color: #757B89;">{{ grid.notification_email }}</div>
              </div>
              
              <div class="grid-actions">
                <!-- <button class="grid-action-btn scan" @click.stop="scanGrid(grid.id)">
                  🔄 Scan Now
                </button> -->
                <button class="grid-action-btn edit" @click.stop="editGrid(grid.id)">
                  ✏️ Edit
                </button>
                <button class="grid-action-btn delete" @click.stop="deleteGrid(grid.id)">
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn secondary" @click="closeGridsListModal">Close</button>
          <button class="action-btn primary" @click="openAddGridFromList">Add New Grid</button>
        </div>
      </div>
    </div>
  </div>
  </div><!-- End of hasActiveSubscription wrapper -->

  <!-- No Active Subscription Message -->
  <div v-else-if="!hasActiveSubscription" class="no-subscription-message">
    <div class="bg-gray-50 rounded-lg p-8 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 mb-2">Subscription Required</h3>
      <p class="text-gray-600 mb-4">Please activate your subscription to access the dashboard and all features.</p>
      <p class="text-sm text-gray-500">Select a plan above to get started.</p>
    </div>
  </div>

</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import $ from 'jquery'
import moment from 'moment'
import SubscriptionStatus from '@/components/SubscriptionStatus.vue'
import ShareModal from '@/components/ShareModal.vue'
export default {
  name: 'Agency',
  components: {
    SubscriptionStatus,
    ShareModal
  },
  data() {
    return {
      // Dashboard stats
      dashboardStats: {
        totalGrids: 0,
        totalGridsChange: 0,
        totalGridsTrend: 'neutral',
        averageRank: 0,
        averageRankChange: 0,
        averageRankTrend: 'neutral',
        top3Percentage: 0,
        top3Change: 0,
        top3Trend: 'neutral',
        gridCoverage: 0,
        gridCoverageChange: 0,
        gridCoverageTrend: 'neutral'
      },
      isLoadingStats: true, // Loading state for dashboard stats
      
      // Search and pagination
      searchQuery: '',
      itemsPerPage: 10,
      currentPage: 1,
      totalItems: 0, // Total count from backend
      selectedRows: [],
      
      // Modal states
      showQuickScanModal: false,
      showAddGridModal: false,
      showGridsListModal: false,
      isLoadingGrids: false,
      gridsError: null,
      showActionsDropdown: false,
      quickScanLoading: false,
      // Share modal
      showShareModal: false,
      shareScanId: null,
      shareScanType: null,
      
      // Quick Scan data
      quickScan: {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '3x3',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridCenter: null,
        // Value SERP specific
        searchProvider: 'google_places', // 'google_places' or 'value_serp'
        showValueSerpSearch: false,
        valueSerpKeyword: '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1,
        searchDebounceTimer: null,
        valueSerpDetailLoading: false
      },
      keywordLimitError: false, 
      // Add Grid data
      addGrid: {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '3x3',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridName: '',
        scanFrequency: 'daily',
        scanTime: 'afternoon',
        emailRecipients: [],
        emailInput: '',
        reportFormat: 'pdf',
        reportName: 'Weekly Ranking Report',
        emailOnComplete: true,
        emailOnChanges: false,
        weeklyReports: false,
        gridCenter: null,
        // Value SERP specific
        searchProvider: 'google_places', // 'google_places' or 'value_serp'
        showValueSerpSearch: false,
        valueSerpKeyword: '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1,
        searchDebounceTimer: null,
        valueSerpDetailLoading: false
      },
      
      // Edit mode properties
      isEditMode: false,
      editingGridId: null,
      
        tableData: [],
        isLoadingScans: false,
        scanError: null,
        gridScans: [], // Array to store grid scans


      
      // Google Maps references
      quickScanMap: null,
      addGridMap: null,
      quickScanAutocomplete: null,
      addGridAutocomplete: null,
      quickScanCompetitorAutocomplete: null,
      addGridCompetitorAutocomplete: null,
      quickScanGridOverlay: null,
      quickScanGridMarkers: [],
      quickScanCenterMarker: null,
      addGridOverlay: null,
      addGridMarkers: [],
      addGridCenterMarker: null,
      googleMapsLoaded: false,
      
      // Search debounce timer
      searchDebounce: null
    }
  },
  
  computed: {
    ...mapGetters('apiKeys', [
      'isGoogleMapsConfigured',
      'isGoogleMapsValid',
      'isValueSerpConfigured',
      'isValueSerpValid'
    ]),
    
    ...mapGetters('auth', [
      'hasActiveSubscription',
      'subscriptionStatus'
    ]),
    
    // Since we're using backend pagination, tableData is already filtered and paginated
    filteredTableData() {
      // For backend pagination, tableData already contains the filtered results
      return this.tableData;
    },
    
    paginatedData() {
      // With backend pagination, tableData is already paginated
      return this.tableData;
    },
    
    totalPages() {
      // Use total count from backend
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    
    isAllSelected() {
      return this.tableData.length > 0 && 
             this.tableData.every(row => this.selectedRows.includes(row.id));
    },
    
    paginationInfo() {
      if (this.totalItems === 0) return 'No data';
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
      return `${start}-${end} of ${this.totalItems}`;
    }
  },
  methods: {
    async fetchDashboardStats() {
      this.isLoadingStats = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/dashboard-stats`,
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          }
        );
        
        if (response.data.success) {
          const stats = response.data.data;
          
          // Update dashboard stats with proper formatting
          this.dashboardStats = {
            totalGrids: stats.totalGrids.value,
            totalGridsChange: stats.totalGrids.change,
            totalGridsTrend: stats.totalGrids.trend,
            
            averageRank: stats.averageRank.value,
            averageRankChange: stats.averageRank.change,
            averageRankTrend: stats.averageRank.trend,
            
            top3Percentage: stats.top3Rankings.value,
            top3Change: stats.top3Rankings.change,
            top3Trend: stats.top3Rankings.trend,
            
            gridCoverage: stats.visibility.value, // Using visibility as grid coverage
            gridCoverageChange: stats.visibility.change,
            gridCoverageTrend: stats.visibility.trend
          };
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        // Set default values on error
        this.dashboardStats = {
          totalGrids: 0,
          totalGridsChange: 0,
          totalGridsChangeLabel: '',
          totalGridsTrend: 'neutral',
          averageRank: 0,
          top3Percentage: 0,
          gridCoverage: 0
        };
      } finally {
        this.isLoadingStats = false;
      }
    },
    
    // Google Maps initialization
    async loadGoogleMapsAPI() {
      if (window.google && window.google.maps) {
        this.googleMapsLoaded = true;
        return;
      }
      
      // Get the user's API key from Vuex store
      const userApiKey = this.$store.getters['apiKeys/googleMapsKey'];
      
      if (!userApiKey) {
        console.error('No Google Maps API key found');
        return Promise.reject('No API key configured');
      }
      
      return new Promise((resolve, reject) => {
        window.initGoogleMaps = () => {
          this.googleMapsLoaded = true;
          resolve();
        };
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${userApiKey}&libraries=geometry,places&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    // Quick Scan Methods
    async openQuickScanModal() {
      // Check if API key is valid
      if (!this.isGoogleMapsValid) {
        this.$swal({
          icon: 'warning',
          title: 'Google Maps API Key Required',
          text: !this.isGoogleMapsConfigured 
            ? 'Please configure your Google Maps API key in Settings to use Quick Scan.' 
            : 'Your Google Maps API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          }
        });
        return;
      }
      
      this.showQuickScanModal = true;
      await this.$nextTick();
      
      if (!this.googleMapsLoaded) {
        await this.loadGoogleMapsAPI();
      }
      
      setTimeout(() => {
        this.initQuickScanMap();
        this.initQuickScanAutocomplete();
        this.initQuickScanCompetitorAutocomplete();
      }, 100);
    },
    
    closeQuickScanModal() {
      this.showQuickScanModal = false;
      this.quickScanLoading = false; // Reset loading state
      this.resetQuickScanForm();
    },
    
    resetQuickScanForm() {
      this.quickScan = {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '5x5',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridCenter: null,
        // Reset Value SERP specific
        searchProvider: 'google_places',
        showValueSerpSearch: false,
        valueSerpKeyword: '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1,
        searchDebounceTimer: null,
        valueSerpDetailLoading: false
      };
    },
    
    // Value SERP Methods
    switchToValueSerp(modalType) {
      // Check if user has valid Value SERP API key from Vuex store
      if (!this.isValueSerpValid) {
        this.$swal({
          icon: 'warning',
          title: 'Value SERP API Key Required',
          text: !this.isValueSerpConfigured 
            ? 'Please configure your Value SERP API key in Settings > Integrations to use this feature.' 
            : 'Your Value SERP API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          }
        });
        return;
      }
      
      // Switch to Value SERP search mode
      if (modalType === 'quickScan') {
        this.quickScan.showValueSerpSearch = true;
        this.quickScan.searchProvider = 'value_serp';
      } else if (modalType === 'addGrid') {
        this.addGrid.showValueSerpSearch = true;
        this.addGrid.searchProvider = 'value_serp';
      }
    },
    
    switchToGooglePlaces(modalType) {
      if (modalType === 'quickScan') {
        this.quickScan.showValueSerpSearch = false;
        this.quickScan.searchProvider = 'google_places';
        this.quickScan.valueSerpSearchResults = [];
        this.quickScan.valueSerpKeyword = '';
        this.quickScan.showValueSerpDropdown = false;
        
        // Initialize Google Places autocomplete for quickScan
        this.$nextTick(() => {
          if (this.$refs.quickScanBusinessSearch) {
            this.initQuickScanAutocomplete();
          }
        });
      } else if (modalType === 'addGrid') {
        this.addGrid.showValueSerpSearch = false;
        this.addGrid.searchProvider = 'google_places';
        this.addGrid.valueSerpSearchResults = [];
        this.addGrid.valueSerpKeyword = '';
        this.addGrid.showValueSerpDropdown = false;
        
        // Initialize Google Places autocomplete for addGrid
        this.$nextTick(() => {
          if (this.$refs.addGridBusinessSearch) {
            this.initAddGridAutocomplete();
          }
          // Also initialize competitor autocomplete
          if (this.$refs.addGridCompetitorSearch) {
            this.initAddGridCompetitorAutocomplete();
          }
        });
      }
    },

    toggleSearchProvider(modalType) {
      const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
      
      if (scanData.showValueSerpSearch) {
        // Switching to Value SERP
        this.switchToValueSerp(modalType);
      } else {
        // Switching back to Google Places
        this.switchToGooglePlaces(modalType);
      }
    },

    onValueSerpInput(modalType) {
      const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
      
      // Clear previous timer
      if (scanData.searchDebounceTimer) {
        clearTimeout(scanData.searchDebounceTimer);
      }
      
      // Don't search if input is too short
      if (scanData.valueSerpKeyword.length < 3) {
        scanData.valueSerpSearchResults = [];
        return;
      }
      
      // Set new timer for debounced search
      scanData.searchDebounceTimer = setTimeout(() => {
        this.performValueSerpSearch(modalType);
      }, 300); // 300ms debounce
    },

    async performValueSerpSearch(modalType) {
      const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
      
      if (!scanData.valueSerpKeyword.trim() || scanData.valueSerpKeyword.length < 3) {
        return;
      }
      
      scanData.valueSerpSearchLoading = true;
      scanData.showValueSerpDropdown = true;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/search`,
          { 
            keyword: scanData.valueSerpKeyword
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );
        
        if (response.data.success && response.data.suggestions) {
          scanData.valueSerpSearchResults = response.data.suggestions.slice(0, 10); // Limit to 10 results
        }
      } catch (error) {
        console.error('Value SERP search error:', error);
        scanData.valueSerpSearchResults = [];
      } finally {
        scanData.valueSerpSearchLoading = false;
      }
    },

    hideValueSerpDropdown(modalType) {
      const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
      // Delay hiding to allow click events to process
      setTimeout(() => {
        scanData.showValueSerpDropdown = false;
        scanData.hoveredIndex = -1;
      }, 200);
    },
    
    async searchValueSerp(modalType) {
      const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
      
      if (!scanData.valueSerpKeyword.trim()) {
        this.$swal({
          icon: 'warning',
          title: 'Search Required',
          text: 'Please enter a business name to search.'
        });
        return;
      }
      
      scanData.valueSerpSearchLoading = true;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/search`,
          { 
            keyword: scanData.valueSerpKeyword
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );
        
        if (response.data.success && response.data.suggestions) {
          scanData.valueSerpSearchResults = response.data.suggestions;
          
          if (response.data.suggestions.length === 0) {
            this.$swal({
              icon: 'info',
              title: 'No Results',
              text: 'No businesses found. Try a different search term.'
            });
          }
        }
      } catch (error) {
        console.error('Value SERP search error:', error);
        this.$swal({
          icon: 'error',
          title: 'Search Failed',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to search businesses. Please try again.'
        });
      } finally {
        scanData.valueSerpSearchLoading = false;
      }
    },
    
    async selectValueSerpBusiness(business, modalType) {
      // Close dropdown immediately
      const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
      scanData.showValueSerpDropdown = false;
      scanData.hoveredIndex = -1;
      
      // Show loading state for business details
      scanData.valueSerpDetailLoading = true;
      
      // Set temporary placeholder while loading
      scanData.selectedBusiness = {
        name: 'Loading...',
        address: 'Fetching business details...',
        placeId: null
      };
      
      try {
        // Get full place details with coordinates
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/place-details`,
          { 
            data_cid: business.data_cid 
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );
        
        if (response.data.success && response.data.data) {
          const placeData = response.data.data;
          // Format the business data similar to Google Places
          const formattedBusiness = {
            name: placeData.title,
            address: placeData.address,
            formatted_address: placeData.address,
            placeId: placeData.place_id ,
            data_cid: business.data_cid,
            location: {
              lat: parseFloat(placeData.latitude),
              lng: parseFloat(placeData.longitude)
            },
            geometry: {
              location: {
                lat: () => parseFloat(placeData.latitude),
                lng: () => parseFloat(placeData.longitude)
              }
            },
            provider: 'value_serp'
          };
          
          if (modalType === 'quickScan') {
            this.quickScan.selectedBusiness = formattedBusiness;
            this.quickScan.valueSerpSearchResults = [];
            this.quickScan.gridCenter = {
              lat: parseFloat(placeData.latitude),
              lng: parseFloat(placeData.longitude)
            };
            
            // Initialize map with this location
            if (this.quickScanMap) {
              this.quickScanMap.setCenter(this.quickScan.gridCenter);
              this.drawQuickScanGrid();
            }
          } else if (modalType === 'addGrid') {
            this.addGrid.selectedBusiness = formattedBusiness;
            this.addGrid.valueSerpSearchResults = [];
            this.addGrid.gridCenter = {
              lat: parseFloat(placeData.latitude),
              lng: parseFloat(placeData.longitude)
            };
            
            // Initialize map with this location
            if (this.addGridMap) {
              this.addGridMap.setCenter(this.addGrid.gridCenter);
              this.drawAddGridGrid();
            }
          }
        }
      } catch (error) {
        console.error('Failed to get place details:', error);
        // Clear the loading placeholder
        const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
        scanData.selectedBusiness = null;
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to get business details. Please try again.'
        });
      } finally {
        // Clear loading state
        const scanData = modalType === 'quickScan' ? this.quickScan : this.addGrid;
        scanData.valueSerpDetailLoading = false;
      }
    },
    
    initQuickScanMap() {
      if (!this.$refs.quickScanGoogleMap || !window.google) return;
      
      const mapCenter = this.quickScan.gridCenter || { lat: 33.5186, lng: -86.8104 };
      
      this.quickScanMap = new window.google.maps.Map(this.$refs.quickScanGoogleMap, {
        zoom: 14,
        center: mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        clickableIcons: false
      });
      
      if (this.quickScan.gridCenter) {
        this.drawQuickScanGrid();
      }
    },
    
    initQuickScanAutocomplete() {
      if (!this.$refs.quickScanBusinessSearch || !window.google) return;
      
      // Remove existing autocomplete if it exists
      if (this.quickScanAutocomplete) {
        window.google.maps.event.clearInstanceListeners(this.quickScanAutocomplete);
        this.quickScanAutocomplete = null;
      }
      
      this.quickScanAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.quickScanBusinessSearch,
        {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types']
        }
      );
      
      this.quickScanAutocomplete.addListener('place_changed', () => {
        const place = this.quickScanAutocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          alert('No location found for this place. Please select from the dropdown.');
          return;
        }
        
        this.quickScan.selectedBusiness = {
          name: place.name || '',
          address: place.formatted_address || '',
          placeId: place.place_id,
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        };
        
        this.quickScan.gridCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        
        if (this.quickScanMap) {
          this.quickScanMap.setCenter(place.geometry.location);
          this.drawQuickScanGrid();
        }
      });
    },
    
    initQuickScanCompetitorAutocomplete() {
      if (!this.$refs.quickScanCompetitorSearch || !window.google) return;
      
      // Remove existing autocomplete if it exists
      if (this.quickScanCompetitorAutocomplete) {
        window.google.maps.event.clearInstanceListeners(this.quickScanCompetitorAutocomplete);
        this.quickScanCompetitorAutocomplete = null;
      }
      
      this.quickScanCompetitorAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.quickScanCompetitorSearch,
        {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry']
        }
      );
      
      this.quickScanCompetitorAutocomplete.addListener('place_changed', () => {
        const place = this.quickScanCompetitorAutocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          return;
        }
        
        const exists = this.quickScan.competitors.some(comp => comp.place_id === place.place_id);
        if (exists) {
          alert('This competitor is already in your list.');
          this.quickScan.competitorSearch = '';
          return;
        }
        
        this.quickScan.competitors.push({
          place_id: place.place_id,
          name: place.name || 'Unknown Business',
          address: place.formatted_address || '',
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        });
        
        this.quickScan.competitorSearch = '';
      });
    },
    
    drawQuickScanGrid() {
      if (!this.quickScanMap || !this.quickScan.gridCenter) return;
      
      // Clear existing grid
      this.clearQuickScanGrid();
      
      const gridSize = this.quickScan.gridSize;
      const radius = parseFloat(this.quickScan.radius);
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
      
      // Convert miles to meters
      const radiusInMeters = radius * 1609.34;
      
      // Calculate grid spacing
      const totalWidth = radiusInMeters * 2;
      const totalHeight = radiusInMeters * 2;
      const cellWidth = totalWidth / (cols - 1);
      const cellHeight = totalHeight / (rows - 1);
      
      // Create bounds for the grid
      const bounds = new window.google.maps.LatLngBounds();
      const gridPoints = [];
      
      // Calculate grid points
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const offsetX = (col - (cols - 1) / 2) * cellWidth;
          const offsetY = ((rows - 1) / 2 - row) * cellHeight;
          
          const point = window.google.maps.geometry.spherical.computeOffset(
            new window.google.maps.LatLng(this.quickScan.gridCenter.lat, this.quickScan.gridCenter.lng),
            Math.sqrt(offsetX * offsetX + offsetY * offsetY),
            Math.atan2(offsetX, offsetY) * 180 / Math.PI
          );
          
          gridPoints.push(point);
          bounds.extend(point);
        }
      }
      this.addGrid.gridPoints = gridPoints;
      // Create blue background as a Rectangle
      this.quickScanGridOverlay = new window.google.maps.Rectangle({
        bounds: bounds,
        map: this.quickScanMap,
        fillColor: '#4f46e5',
        fillOpacity: 0.15,
        strokeColor: '#4f46e5',
        strokeOpacity: 0.3,
        strokeWeight: 2,
        draggable: false,
        geodesic: false,
        zIndex: 1
      });
      
      // Create grid point markers
      let markerIndex = 1;
      gridPoints.forEach((point) => {
        const marker = new window.google.maps.Marker({
          position: point,
          map: this.quickScanMap,
          label: {
            text: '?',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 'bold'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#4f46e5',
            fillOpacity: 0.8,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 15
          },
          title: `Grid Point ${markerIndex} - Rank Unknown`,
          zIndex: 1000
        });
        
        this.quickScanGridMarkers.push(marker);
        markerIndex++;
      });
      
      // Add center marker
      if (this.quickScanCenterMarker) {
        this.quickScanCenterMarker.setMap(null);
      }
      
      this.quickScanCenterMarker = new window.google.maps.Marker({
        position: new window.google.maps.LatLng(this.quickScan.gridCenter.lat, this.quickScan.gridCenter.lng),
        map: this.quickScanMap,
        title: 'Drag to move grid',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="#ef4444" stroke="#FFFFFF" stroke-width="2"/>' +
            '<circle cx="12" cy="10" r="3" fill="#FFFFFF"/>' +
            '</svg>'
          ),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 40)
        },
        draggable: true,
        cursor: 'move',
        zIndex: 1001
      });
      
      // Add drag listeners
      this.quickScanCenterMarker.addListener('drag', (event) => {
        this.quickScan.gridCenter = { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        };
        this.updateQuickScanGridWhileDragging();
      });
      
      this.quickScanCenterMarker.addListener('dragend', (event) => {
        this.quickScan.gridCenter = { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        };
        this.drawQuickScanGrid();
      });
      
      // Adjust map to show entire grid
      this.quickScanMap.fitBounds(bounds);
      const currentZoom = this.quickScanMap.getZoom();
      if (currentZoom > 15) {
        this.quickScanMap.setZoom(15);
      }
    },
    
    updateQuickScanGridWhileDragging() {
      if (!this.quickScanMap || !this.quickScan.gridCenter) return;
      
      const gridSize = this.quickScan.gridSize;
      const radius = parseFloat(this.quickScan.radius);
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
      
      const radiusInMeters = radius * 1609.34;
      const totalWidth = radiusInMeters * 2;
      const totalHeight = radiusInMeters * 2;
      const cellWidth = totalWidth / (cols - 1);
      const cellHeight = totalHeight / (rows - 1);
      
      const bounds = new window.google.maps.LatLngBounds();
      
      let markerIndex = 0;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          if (markerIndex < this.quickScanGridMarkers.length) {
            const offsetX = (col - (cols - 1) / 2) * cellWidth;
            const offsetY = ((rows - 1) / 2 - row) * cellHeight;
            
            const point = window.google.maps.geometry.spherical.computeOffset(
              new window.google.maps.LatLng(this.quickScan.gridCenter.lat, this.quickScan.gridCenter.lng),
              Math.sqrt(offsetX * offsetX + offsetY * offsetY),
              Math.atan2(offsetX, offsetY) * 180 / Math.PI
            );
            
            this.quickScanGridMarkers[markerIndex].setPosition(point);
            bounds.extend(point);
            markerIndex++;
          }
        }
      }
      
      if (this.quickScanGridOverlay) {
        this.quickScanGridOverlay.setBounds(bounds);
      }
    },
    
    clearQuickScanGrid() {
      // Clear markers
      this.quickScanGridMarkers.forEach(marker => {
        marker.setMap(null);
      });
      this.quickScanGridMarkers = [];
      
      // Clear overlay
      if (this.quickScanGridOverlay) {
        this.quickScanGridOverlay.setMap(null);
      }
      
      // Clear center marker
      if (this.quickScanCenterMarker) {
        this.quickScanCenterMarker.setMap(null);
      }
    },
    
    updateQuickScanPreview() {
      if (this.quickScanMap && this.quickScan.gridCenter) {
        this.drawQuickScanGrid();
      }
    },
    
    // Keyword management for Quick Scan
    //commnet if multiple keywords required in quick scan
    // addKeyword() {
    //   const keyword = this.quickScan.keywordInput.trim();
    //   if (keyword && !this.quickScan.keywords.includes(keyword)) {
    //     this.quickScan.keywords.push(keyword);
    //     this.quickScan.keywordInput = '';
    //   }
    // },
    addKeyword() {
      const keyword = this.quickScan.keywordInput.trim();
      if (!keyword) return;

      if (this.quickScan.keywords.length >= 1) {
        this.keywordLimitError = true; 
        setTimeout(() => {
          this.keywordLimitError = false;
        }, 1000);
        return;
      }

      this.quickScan.keywords = [keyword];
      this.quickScan.keywordInput = '';
      this.keywordLimitError = false; 
    },
    removeKeyword(index) {
      this.quickScan.keywords.splice(index, 1);
    },
    
    editKeyword(index, event) {
      const newValue = event.target.textContent.trim();
      if (newValue) {
        this.quickScan.keywords[index] = newValue;
      } else {
        this.removeKeyword(index);
      }
    },
    // comment if multiple keywords required
    // handleKeywordInput() {
    //   const value = this.quickScan.keywordInput;
    //   if (value.includes(',')) {
    //     const keyword = value.replace(',', '').trim();
    //     if (keyword && !this.quickScan.keywords.includes(keyword)) {
    //       this.quickScan.keywords.push(keyword);
    //       this.quickScan.keywordInput = '';
    //     }
    //   }
    // },
      handleKeywordInput() {
        const value = this.quickScan.keywordInput;
        if (value.includes(',')) {
          const keyword = value.replace(',', '').trim();
          if (!keyword) return;

          if (this.quickScan.keywords.length >= 1) {
            this.keywordLimitError = true; 
            setTimeout(() => {
              this.keywordLimitError = false;
            }, 1000);
            return;
          }

          this.quickScan.keywords = [keyword];
          this.quickScan.keywordInput = '';
          this.keywordLimitError = false; 
        }
      },
    // Competitor management for Quick Scan
    removeCompetitor(index) {
      this.quickScan.competitors.splice(index, 1);
    },
    
    // Start Quick Scan
    async startQuickScan() {
      if (this.quickScan.keywords.length === 0) {
        alert('Please enter at least one keyword.');
        return;
      }
      
      if (!this.quickScan.selectedBusiness) {
        alert('Please search and select your business from the dropdown.');
        return;
      }
      
      // Set loading state to true
      this.quickScanLoading = true;
      
      const scanData = {
        search_type: 'quick_scan',
        business_name: this.quickScan.selectedBusiness.name,
        place_id: this.quickScan.selectedBusiness.placeId,
        business: this.quickScan.selectedBusiness,
        keywords: this.quickScan.keywords,
        competitors: this.quickScan.competitors, // Include competitors
        grid_size: this.quickScan.gridSize, //gridSize
        radius: this.quickScan.radius,
        distance_type: 'miles',
        address: this.quickScan.selectedBusiness.address,
        latitude: (this.quickScan.gridCenter && this.quickScan.gridCenter.lat) || this.quickScan.selectedBusiness.location.lat,
        longitude: (this.quickScan.gridCenter && this.quickScan.gridCenter.lng) || this.quickScan.selectedBusiness.location.lng,
        grid_points: this.addGrid.gridPoints || [],
        search_provider: this.quickScan.searchProvider || 'google_places', // Add search provider
        timestamp: new Date().toLocaleString()
      };

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans`, 
          scanData,
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`, // JWT token
              "Content-Type": "application/json"
            }
          }
        );
        
        // Track quick scan initiated event
        this.$posthog.capture('quick_scan_initiated', {
          keyword_count: this.quickScan.keywords.length,
          grid_size: this.quickScan.gridSize,
          radius: this.quickScan.radius,
          business_name: this.quickScan.selectedBusiness.name,
          competitors_count: this.quickScan.competitors ? this.quickScan.competitors.length : 0,
          location_count: this.addGrid.gridPoints ? this.addGrid.gridPoints.length : 0
        });
        
        this.closeQuickScanModal();
        // Create the route URL
        const routeUrl = this.$router.resolve({
          name: 'QuickScanDetails', // Assuming you have a route name
          params: { id: response.data.search_id }
        });
        
        // Open in new tab
        window.open(routeUrl.href, '_blank');
        // Reload page after successful submit
        window.location.reload();

      } catch (error) {
        console.error("Error while starting scan:",(error.response && error.response.data) || error.message);
        alert("Error while starting scan:",(error.response && error.response.data) || error.message);
        // Reset loading state on error
        this.quickScanLoading = false;
      }
    },
    // Add Grid Methods
    async openAddGridModal() {
      // Check if API key is valid
      if (!this.isGoogleMapsValid) {
        this.$swal({
          icon: 'warning',
          title: 'Google Maps API Key Required',
          text: !this.isGoogleMapsConfigured 
            ? 'Please configure your Google Maps API key in Settings to use Grid features.' 
            : 'Your Google Maps API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          }
        });
        return;
      }
      
      this.showAddGridModal = true;
      await this.$nextTick();
      
      if (!this.googleMapsLoaded) {
        await this.loadGoogleMapsAPI();
      }
      
      setTimeout(() => {
        this.initAddGridMap();
        this.initAddGridAutocomplete();
        this.initAddGridCompetitorAutocomplete();
      }, 100);
    },
    
    closeAddGridModal() {
      this.showAddGridModal = false;
      // Reset edit mode
      this.isEditMode = false;
      this.editingGridId = null;
      this.resetAddGridForm();
    },
    
    resetAddGridForm() {
      // Reset edit mode
      this.isEditMode = false;
      this.editingGridId = null;
      
      this.addGrid = {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '5x5',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridName: '',
        scanFrequency: 'daily',
        scanTime: 'afternoon',
        emailRecipients: [],
        emailInput: '',
        reportFormat: 'pdf',
        reportName: 'Weekly Ranking Report',
        emailOnComplete: true,
        emailOnChanges: false,
        weeklyReports: false,
        gridCenter: null
      };
    },
    
    initAddGridMap() {
      if (!this.$refs.addGridGoogleMap || !window.google) return;
      
      const mapCenter = this.addGrid.gridCenter || { lat: 33.5186, lng: -86.8104 };
      
      this.addGridMap = new window.google.maps.Map(this.$refs.addGridGoogleMap, {
        zoom: 14,
        center: mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        clickableIcons: false
      });
      
      if (this.addGrid.gridCenter) {
        this.drawAddGridGrid();
      }
    },
    
    initAddGridAutocomplete() {
      if (!this.$refs.addGridBusinessSearch || !window.google) return;
      
      // Remove existing autocomplete if it exists
      if (this.addGridAutocomplete) {
        window.google.maps.event.clearInstanceListeners(this.addGridAutocomplete);
        this.addGridAutocomplete = null;
      }
      
      this.addGridAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.addGridBusinessSearch,
        {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types']
        }
      );
      
      this.addGridAutocomplete.addListener('place_changed', () => {
        const place = this.addGridAutocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          alert('No location found for this place. Please select from the dropdown.');
          return;
        }
        
        this.addGrid.selectedBusiness = {
          name: place.name || '',
          address: place.formatted_address || '',
          placeId: place.place_id,
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        };
        
        this.addGrid.gridCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        
        if (this.addGridMap) {
          this.addGridMap.setCenter(place.geometry.location);
          this.drawAddGridGrid();
        }
      });
    },
    
    initAddGridCompetitorAutocomplete() {
      if (!this.$refs.addGridCompetitorSearch || !window.google) return;
      
      // Remove existing autocomplete if it exists
      if (this.addGridCompetitorAutocomplete) {
        window.google.maps.event.clearInstanceListeners(this.addGridCompetitorAutocomplete);
        this.addGridCompetitorAutocomplete = null;
      }
      
      this.addGridCompetitorAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.addGridCompetitorSearch,
        {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry']
        }
      );
      
      this.addGridCompetitorAutocomplete.addListener('place_changed', () => {
        const place = this.addGridCompetitorAutocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          return;
        }
        
        const exists = this.addGrid.competitors.some(comp => comp.place_id === place.place_id);
        if (exists) {
          alert('This competitor is already in your list.');
          this.addGrid.competitorSearch = '';
          return;
        }
        
        this.addGrid.competitors.push({
          place_id: place.place_id,
          name: place.name || 'Unknown Business',
          address: place.formatted_address || '',
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        });
        
        this.addGrid.competitorSearch = '';
      });
    },
    
    drawAddGridGrid() {
      if (!this.addGridMap || !this.addGrid.gridCenter) return;
      
      // Clear existing grid
      this.clearAddGridGrid();
      
      const gridSize = this.addGrid.gridSize;
      const radius = parseFloat(this.addGrid.radius);
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
      
      const radiusInMeters = radius * 1609.34;
      const totalWidth = radiusInMeters * 2;
      const totalHeight = radiusInMeters * 2;
      const cellWidth = totalWidth / (cols - 1);
      const cellHeight = totalHeight / (rows - 1);
      
      const bounds = new window.google.maps.LatLngBounds();
      const gridPoints = [];
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const offsetX = (col - (cols - 1) / 2) * cellWidth;
          const offsetY = ((rows - 1) / 2 - row) * cellHeight;
          
          const point = window.google.maps.geometry.spherical.computeOffset(
            new window.google.maps.LatLng(this.addGrid.gridCenter.lat, this.addGrid.gridCenter.lng),
            Math.sqrt(offsetX * offsetX + offsetY * offsetY),
            Math.atan2(offsetX, offsetY) * 180 / Math.PI
          );
          
          gridPoints.push(point);
          bounds.extend(point);
        }
      }
      
      this.addGridOverlay = new window.google.maps.Rectangle({
        bounds: bounds,
        map: this.addGridMap,
        fillColor: '#4f46e5',
        fillOpacity: 0.15,
        strokeColor: '#4f46e5',
        strokeOpacity: 0.3,
        strokeWeight: 2,
        draggable: false,
        geodesic: false,
        zIndex: 1
      });
      
      let markerIndex = 1;
      gridPoints.forEach((point) => {
        const marker = new window.google.maps.Marker({
          position: point,
          map: this.addGridMap,
          label: {
            text: '?',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 'bold'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#4f46e5',
            fillOpacity: 0.8,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 15
          },
          title: `Grid Point ${markerIndex} - Rank Unknown`,
          zIndex: 1000
        });
        
        this.addGridMarkers.push(marker);
        markerIndex++;
      });
      
      if (this.addGridCenterMarker) {
        this.addGridCenterMarker.setMap(null);
      }
      
      this.addGridCenterMarker = new window.google.maps.Marker({
        position: new window.google.maps.LatLng(this.addGrid.gridCenter.lat, this.addGrid.gridCenter.lng),
        map: this.addGridMap,
        title: 'Drag to move grid',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="#ef4444" stroke="#FFFFFF" stroke-width="2"/>' +
            '<circle cx="12" cy="10" r="3" fill="#FFFFFF"/>' +
            '</svg>'
          ),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 40)
        },
        draggable: true,
        cursor: 'move',
        zIndex: 1001
      });
      
      // Add drag listeners
      this.addGridCenterMarker.addListener('drag', (event) => {
        this.addGrid.gridCenter = { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        };
        this.updateAddGridGridWhileDragging();
      });
      
      this.addGridCenterMarker.addListener('dragend', (event) => {
        this.addGrid.gridCenter = { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        };
        this.drawAddGridGrid();
      });
      
      this.addGridMap.fitBounds(bounds);
      const currentZoom = this.addGridMap.getZoom();
      if (currentZoom > 15) {
        this.addGridMap.setZoom(15);
      }
    },
    
    updateAddGridGridWhileDragging() {
      if (!this.addGridMap || !this.addGrid.gridCenter) return;
      
      const gridSize = this.addGrid.gridSize;
      const radius = parseFloat(this.addGrid.radius);
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
      
      const radiusInMeters = radius * 1609.34;
      const totalWidth = radiusInMeters * 2;
      const totalHeight = radiusInMeters * 2;
      const cellWidth = totalWidth / (cols - 1);
      const cellHeight = totalHeight / (rows - 1);
      
      const bounds = new window.google.maps.LatLngBounds();
      
      let markerIndex = 0;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          if (markerIndex < this.addGridMarkers.length) {
            const offsetX = (col - (cols - 1) / 2) * cellWidth;
            const offsetY = ((rows - 1) / 2 - row) * cellHeight;
            
            const point = window.google.maps.geometry.spherical.computeOffset(
              new window.google.maps.LatLng(this.addGrid.gridCenter.lat, this.addGrid.gridCenter.lng),
              Math.sqrt(offsetX * offsetX + offsetY * offsetY),
              Math.atan2(offsetX, offsetY) * 180 / Math.PI
            );
            
            this.addGridMarkers[markerIndex].setPosition(point);
            bounds.extend(point);
            markerIndex++;
          }
        }
      }
      
      if (this.addGridOverlay) {
        this.addGridOverlay.setBounds(bounds);
      }
    },
    
    clearAddGridGrid() {
      this.addGridMarkers.forEach(marker => {
        marker.setMap(null);
      });
      this.addGridMarkers = [];
      
      if (this.addGridOverlay) {
        this.addGridOverlay.setMap(null);
      }
      
      if (this.addGridCenterMarker) {
        this.addGridCenterMarker.setMap(null);
      }
    },
    
    updateAddGridPreview() {
      if (this.addGridMap && this.addGrid.gridCenter) {
        this.drawAddGridGrid();
      }
    },
    
    // Add Grid keyword management
    addAddGridKeyword() {
      const keyword = this.addGrid.keywordInput.trim();
      if (keyword && !this.addGrid.keywords.includes(keyword)) {
        this.addGrid.keywords.push(keyword);
        this.addGrid.keywordInput = '';
      }
    },
    
    removeAddGridKeyword(index) {
      this.addGrid.keywords.splice(index, 1);
    },
    
    editAddGridKeyword(index, event) {
      const newValue = event.target.textContent.trim();
      if (newValue) {
        this.addGrid.keywords[index] = newValue;
      } else {
        this.removeAddGridKeyword(index);
      }
    },
    
    handleAddGridKeywordInput() {
      const value = this.addGrid.keywordInput;
      if (value.includes(',')) {
        const keyword = value.replace(',', '').trim();
        if (keyword && !this.addGrid.keywords.includes(keyword)) {
          this.addGrid.keywords.push(keyword);
          this.addGrid.keywordInput = '';
        }
      }
    },
    
    // Add Grid competitor management
    removeAddGridCompetitor(index) {
      this.addGrid.competitors.splice(index, 1);
    },
    
    // Email management
    addEmailRecipient() {
      const email = this.addGrid.emailInput.trim();
      if (email && this.isValidEmail(email)) {
        if (this.addGrid.emailRecipients.includes(email)) {
          alert('This email address is already in the list.');
        } else {
          this.addGrid.emailRecipients.push(email);
          this.addGrid.emailInput = '';
        }
      } else if (email) {
        alert('Please enter a valid email address.');
      }
    },
    
    removeEmailRecipient(index) {
      this.addGrid.emailRecipients.splice(index, 1);
    },
    
    handleEmailInput() {
      const value = this.addGrid.emailInput;
      if (value.includes(',') || value.includes(';')) {
        const email = value.replace(/[,;]/g, '').trim();
        if (email && this.isValidEmail(email)) {
          if (!this.addGrid.emailRecipients.includes(email)) {
            this.addGrid.emailRecipients.push(email);
          }
          this.addGrid.emailInput = '';
        }
      }
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    
    // Save New Grid
    async saveNewGrid() {
      // Validation
      if (!this.addGrid.selectedBusiness) {
        this.$swal({
          icon: 'warning',
          title: 'Business Required',
          text: 'Please search and select your business from the dropdown.'
        });
        return;
      }
      
      if (this.addGrid.keywords.length === 0) {
        this.$swal({
          icon: 'warning',
          title: 'Keywords Required',
          text: 'Please enter at least one keyword.'
        });
        return;
      }

      if (!this.addGrid.gridName) {
        this.$swal({
          icon: 'warning',
          title: 'Grid Name Required',
          text: 'Please enter a grid name.'
        });
        return;
      }

      if (!this.addGrid.scanFrequency) {
        this.$swal({
          icon: 'warning',
          title: 'Scan Frequency Required',
          text: 'Please select scan frequency.'
        });
        return;
      }

      // For Manual frequency, scan_time is not required and should be null
      if (this.addGrid.scanFrequency !== 'manual' && !this.addGrid.scanTime) {
        this.$swal({
          icon: 'warning',
          title: 'Scan Time Required',
          text: 'Please select scan time for scheduled scans.'
        });
        return;
      }

      // Convert scan time to HH:MM format
      let scanTimeFormatted = null; // Default to null for manual
      
      // Only process scan time if not manual frequency
      if (this.addGrid.scanFrequency !== 'manual') {
        // Map time options to actual times
        const timeMapping = {
          'morning': '09:00',
          'afternoon': '14:00',
          'evening': '18:00',
          'night': '21:00'
        };

        // If scanTime is in the format above, map it, otherwise use as is
        if (timeMapping[this.addGrid.scanTime]) {
          scanTimeFormatted = timeMapping[this.addGrid.scanTime];
        } else if (this.addGrid.scanTime && this.addGrid.scanTime.includes(':')) {
          // If already in HH:MM format
          scanTimeFormatted = this.addGrid.scanTime;
        } else {
          scanTimeFormatted = '09:00'; // Default to 9 AM if not set
        }
      }

      // Generate grid points if not already generated
      if (!this.addGrid.gridPoints || this.addGrid.gridPoints.length === 0) {
        await this.generateGridPoints();
      }

      // Prepare data for API
      const gridScanData = {
        // Business Information
        business_name: this.addGrid.selectedBusiness.name,
        business: this.addGrid.selectedBusiness,
        address: this.addGrid.selectedBusiness.address,
        latitude: (this.addGrid.gridCenter && this.addGrid.gridCenter.lat) || (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lat) || this.addGrid.selectedBusiness.latitude,
        longitude: (this.addGrid.gridCenter && this.addGrid.gridCenter.lng) || (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lng) || this.addGrid.selectedBusiness.longitude,
        
        // Grid Configuration
        grid_name: this.addGrid.gridName,
        keywords: this.addGrid.keywords,
        grid_size: this.addGrid.gridSize,
        radius: this.addGrid.radius,
        distance_type: 'miles', // You can make this configurable if needed
        grid_points: this.addGrid.gridPoints || [],
        search_provider: this.addGrid.searchProvider || 'google_places', // Add search provider
        
        // Scheduling Information
        scan_frequency: this.addGrid.scanFrequency,
        scan_time: scanTimeFormatted,
        notification_email: this.addGrid.emailRecipients.join(','), // Convert array to comma-separated string
        
        // ADD USER TIMEZONE - This automatically detects the user's timezone
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        
        // Additional metadata (optional, but useful)
        competitors: this.addGrid.competitors,
        report_format: this.addGrid.reportFormat,
        report_name: this.addGrid.reportName,
        email_on_complete: this.addGrid.emailOnComplete,
        email_on_changes: this.addGrid.emailOnChanges,
        weekly_reports: this.addGrid.weeklyReports,
        
        // Flag to trigger immediate scan for manual frequency
        trigger_immediately: this.addGrid.scanFrequency === 'manual'
      };

      

      try {
        // Determine if this is create or update
        const isUpdate = this.isEditMode && this.editingGridId;
        
        // Call the appropriate API endpoint
        const response = isUpdate 
          ? await axios.put(
              `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${this.editingGridId}`,
              gridScanData,
              {
                headers: {
                  Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
                  "Content-Type": "application/json"
                }
              }
            )
          : await axios.post(
              `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan`,
              gridScanData,
              {
                headers: {
                  Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
                  "Content-Type": "application/json"
                }
              }
            );

        if (response.data.success) {
          // Track grid scan creation/update event
          if (!isUpdate) {
            this.$posthog.capture('grid_scan_created', {
              grid_size: this.addGrid.gridSize,
              keyword_count: this.addGrid.keywords.length,
              location_count: this.addGrid.gridPoints ? this.addGrid.gridPoints.length : 0,
              scan_frequency: this.addGrid.scanFrequency,
              business_name: this.addGrid.selectedBusiness.name,
              competitors_count: this.addGrid.competitors ? this.addGrid.competitors.length : 0,
              radius: this.addGrid.radius
            });
          }
          
          // Show success message based on operation and frequency type
          let successMessage;
          let detailedMessage = '';
          
          if (isUpdate) {
            // Use the message from backend which includes next run info
            successMessage = response.data.message || `Grid scan "${this.addGrid.gridName}" has been updated successfully.`;
            
            // Add additional clarification for scheduled scans
            if (this.addGrid.scanFrequency !== 'manual' && response.data.next_run) {
              detailedMessage = '\n\n⏰ Note: These changes will take effect from the next scheduled run, not on historical data.';
            }
          } else if (this.addGrid.scanFrequency === 'manual') {
            successMessage = `Your grid scan "${this.addGrid.gridName}" has been created and is running now! You can trigger it manually anytime using "Scan Again".`;
          } else {
            successMessage = `Your grid scan "${this.addGrid.gridName}" has been scheduled successfully. Next run: ${new Date(response.data.next_run).toLocaleString()}`;
          }
          
          this.$swal({
            icon: 'success',
            title: isUpdate ? 'Grid Scan Updated!' : 'Grid Scan Created!',
            html: `<div style="text-align: left;">
              <p>${successMessage}</p>
              ${detailedMessage ? `<p style="color: #6b7280; font-size: 0.9em; margin-top: 10px;">${detailedMessage}</p>` : ''}
            </div>`,
            showConfirmButton: true,
            confirmButtonText: 'Got it!',
            timer: isUpdate ? null : 5000 // Don't auto-close for updates so user can read the message
          });

          // Reset form
          this.resetAddGridForm();
          
          // Close modal
          this.closeAddGridModal();
          
          // Refresh grid list if the modal was open
          if (this.gridScans && this.gridScans.length > 0) {
            await this.fetchUserGridScans();
          }
          
          // Optional: Navigate to grid scan details or dashboard
          // this.$router.push({ name: 'GridScanDetails', params: { id: response.data.search_id } });
          
        } else {
          throw new Error(response.data.message || 'Failed to create grid scan');
        }

      } catch (error) {
        console.error('Error creating grid scan:', error);
        
        // Show error message
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to create grid scan. Please try again.',
          showConfirmButton: true
        });
      }
    },

    // Helper function to reset the form
    resetAddGridForm() {
      this.addGrid = {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '3x3',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridName: '',
        scanFrequency: 'daily',
        scanTime: 'afternoon',
        emailRecipients: [],
        emailInput: '',
        reportFormat: 'pdf',
        reportName: 'Weekly Ranking Report',
        emailOnComplete: true,
        emailOnChanges: false,
        weeklyReports: false,
        gridCenter: null,
        gridPoints: [],
        
        // Value SERP specific fields (reset to defaults)
        showValueSerpSearch: false,
        searchProvider: 'google_places',
        valueSerpKeyword: '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        valueSerpDetailLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1
      };
    },

    // Helper function to generate grid points (if not already implemented)
    async generateGridPoints() {
      if (!this.addGrid.selectedBusiness) return;
      
      const center = {
        lat: (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lat) || this.addGrid.selectedBusiness.latitude,
        lng: (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lng) || this.addGrid.selectedBusiness.longitude
      };
      
      // Parse grid size (e.g., "3x3" -> 3)
      const gridDimension = parseInt(this.addGrid.gridSize.split('x')[0]);
      // Distance between pins (NOT radius) - this is the exact gap between adjacent pins
      const distanceBetweenPins = parseFloat(this.addGrid.radius);

      // Generate grid points
      const points = [];
      // Step is exactly the distance between pins
      const step = distanceBetweenPins;
      const halfGrid = (gridDimension - 1) / 2;

      for (let i = 0; i < gridDimension; i++) {
        for (let j = 0; j < gridDimension; j++) {
          const latOffset = (i - halfGrid) * step / 69; // 1 degree latitude ≈ 69 miles
          const lngOffset = (j - halfGrid) * step / (69 * Math.cos(center.lat * Math.PI / 180));

          points.push({
            lat: center.lat + latOffset,
            lng: center.lng + lngOffset
          });
        }
      }

      this.addGrid.gridPoints = points;
    },
    
    // Grids List Modal
    async openGridsListModal() {
      this.showGridsListModal = true;
      await this.fetchUserGridScans();
    },
    
    closeGridsListModal() {
      this.showGridsListModal = false;
      this.gridsError = null;
    },
    
    // Fetch user's grid scans from API
    async fetchUserGridScans() {
      this.isLoadingGrids = true;
      this.gridsError = null;
      
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scans`, {
          headers: {
            Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
            "Content-Type": "application/json"
          }
        });
        
        if (response.data.success) {
          // Transform API data to match our component structure
          this.gridScans = response.data.data.map(scan => ({
            id: scan.id,
            grid_name: scan.grid_name,
            business_name: scan.business_name,
            business: scan.business,
            address: scan.address,
            keywords: scan.keywords,
            grid_size: scan.grid_size,
            radius: scan.radius,
            competitors: scan.competitors,
            scan_frequency: scan.scan_frequency,
            scan_time: scan.scan_time,
            next_run_at: scan.next_run_at,
            last_run_at: scan.last_run_at,
            status: scan.status,
            notification_email: scan.notification_email,
            search_provider: scan.search_provider || 'google_places'
          }));
          
        } else {
          this.gridsError = 'Failed to fetch grid scans';
        }
        
      } catch (error) {
        console.error('Error fetching grid scans:', error);
        this.gridsError = 'Unable to load grid scans. Please try again.';
      } finally {
        this.isLoadingGrids = false;
      }
    },
    
    openAddGridFromList() {
      this.closeGridsListModal();
      this.openAddGridModal();
    },
    
    // Grid actions
    viewGridDetails(gridId) {
      // Open grid scan details page in new tab
      const routeData = this.$router.resolve({
        name: 'GridScanDetails',
        params: { id: gridId }
      });
      window.open(routeData.href, '_blank');
      
      // Close the modal
      this.closeGridsListModal();
    },
    
    scanGrid(gridId) {
      const grid = this.gridScans.find(g => g.id === gridId);
      
      // TODO: Implement manual scan trigger
      alert(`Scan Now functionality will be implemented soon for: ${grid.grid_name}`);
    },
    
    editGrid(gridId) {
      const grid = this.gridScans.find(g => g.id === gridId);
      if (!grid) {
        this.$swal({
          icon: 'error',
          title: 'Grid Not Found',
          text: 'Unable to find the selected grid.'
        });
        return;
      }

      // Set edit mode
      this.isEditMode = true;
      this.editingGridId = gridId;

      // Parse business data - handle if it's null or undefined
      let businessData = {};
      try {
        if (grid.business) {
          businessData = typeof grid.business === 'string' ? JSON.parse(grid.business) : grid.business;
        }
      } catch (e) {
        console.error('Error parsing business data:', e);
        businessData = {};
      }
      

      
      // Parse keywords safely
      let keywords = [];
      try {
        if (grid.keywords) {
          keywords = typeof grid.keywords === 'string' ? JSON.parse(grid.keywords) : grid.keywords;
        }
      } catch (e) {
        console.error('Error parsing keywords:', e);
        keywords = [];
      }
      
      // Pre-fill the form with existing grid data
      this.addGrid = {
        // Keywords
        keywords: keywords || [],
        keywordInput: '',
        
        // Business information - prioritize businessData.name over grid_name
        businessSearch: businessData.name || grid.business_name || grid.grid_name || '',
        selectedBusiness: {
          name: businessData.name || grid.business_name || grid.grid_name || '',
          address: businessData.address || grid.address || businessData.formatted_address || '',
          placeId: businessData.placeId || businessData.place_id || '',
          location: {
            lat: businessData.latitude || (businessData.location && businessData.location.lat) || 0,
            lng: businessData.longitude || (businessData.location && businessData.location.lng) || 0
          },
          latitude: businessData.latitude || (businessData.location && businessData.location.lat) || 0,
          longitude: businessData.longitude || (businessData.location && businessData.location.lng) || 0,
          grid_points: businessData.grid_points || [],
          source: businessData.source || (grid.search_provider === 'value_serp' ? 'value_serp' : 'google_places')
        },
        
        // Grid configuration
        gridSize: grid.grid_size || '3x3',
        radius: grid.radius ? grid.radius.toString() : '1',
        gridName: grid.grid_name || '',
        
        // Scheduling - handle both manual and scheduled types
        scanFrequency: grid.scan_frequency || 'manual',
        scanTime: this.parseScanTime(grid.scan_time),
        
        // Email settings - convert comma-separated string to array
        emailRecipients: grid.notification_email ? grid.notification_email.split(',').map(email => email.trim()) : [],
        emailInput: '',
        
        // Competitors - parse business_data from each competitor
        competitors: grid.competitors ? grid.competitors.map(comp => {
          // Parse business_data if it's a string
          let data = {};
          try {
            if (comp.business_data) {
              data = typeof comp.business_data === 'string' ? JSON.parse(comp.business_data) : comp.business_data;
            }
          } catch (e) {
            console.error('Error parsing competitor business_data:', e);
            data = comp;
          }
          
          return {
            place_id: comp.place_id,
            placeId: comp.place_id,
            name: comp.name,
            address: data.address || data.formatted_address || '',
            location: data.location || { lat: data.latitude || 0, lng: data.longitude || 0 },
            latitude: data.latitude || (data.location && data.location.lat) || 0,
            longitude: data.longitude || (data.location && data.location.lng) || 0
          };
        }) : [],
        competitorSearch: '',
        
        // Report settings (these might not be in the database, use defaults)
        reportFormat: grid.report_format || 'pdf',
        reportName: grid.report_name || 'Weekly Ranking Report',
        emailOnComplete: grid.email_on_complete !== undefined ? grid.email_on_complete : true,
        emailOnChanges: grid.email_on_changes !== undefined ? grid.email_on_changes : false,
        weeklyReports: grid.weekly_reports !== undefined ? grid.weekly_reports : false,
        
        // Grid points and center - set gridCenter for map preview
        gridCenter: {
          lat: businessData.latitude || (businessData.location && businessData.location.lat) || 0,
          lng: businessData.longitude || (businessData.location && businessData.location.lng) || 0
        },
        gridPoints: businessData.grid_points || [],
        
        // Value SERP specific fields
        showValueSerpSearch: grid.search_provider === 'value_serp',
        searchProvider: grid.search_provider || 'google_places',
        valueSerpKeyword: grid.search_provider === 'value_serp' ? (businessData.name || grid.business_name || '') : '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        valueSerpDetailLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1
      };

      // Close the grids list modal and open the add/edit modal
      this.showGridsListModal = false;
      this.showAddGridModal = true;
      
      // Initialize the map after modal opens and draw grid
      this.$nextTick(() => {
        this.initAddGridMap();
        
        // Only initialize Google Places autocomplete if not using Value SERP
        if (!this.addGrid.showValueSerpSearch) {
          this.initAddGridAutocomplete();
          this.initAddGridCompetitorAutocomplete();
        }
        
        // Draw the grid if we have a center
        if (this.addGrid.gridCenter && this.addGrid.gridCenter.lat && this.addGrid.gridCenter.lng) {
          setTimeout(() => {
            this.drawAddGridGrid();
          }, 500);
        }
      });
    },
    
    parseScanTime(scanTime) {
      if (!scanTime) return 'afternoon';
      
      // Map time ranges to options
      const hour = parseInt(scanTime.split(':')[0]);
      if (hour >= 6 && hour < 12) return 'morning';
      if (hour >= 12 && hour < 17) return 'afternoon';
      if (hour >= 17 && hour < 21) return 'evening';
      return 'night';
    },
    
    async deleteGrid(gridId) {
      const grid = this.gridScans.find(g => g.id === gridId);
      
      const result = await this.$swal({
        title: 'Confirm Delete',
        text: `Are you sure you want to delete "${grid.grid_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });
      
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete`, {
            data: { scanIds: [gridId] }, // Send as array even for single delete
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          });
          
          if (response.data.success) {
            this.$swal({
              icon: 'success',
              title: 'Deleted!',
              text: `Grid scan "${grid.grid_name}" has been deleted.`,
              timer: 2000,
              showConfirmButton: false
            });
            
            // Refresh the grid scans list
            await this.fetchUserGridScans();
          } else {
            throw new Error(response.data.message);
          }
        } catch (error) {
          console.error('Error deleting grid scan:', error);
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to delete grid scan'
          });
        }
      }
    },
    
    // Helper functions for formatting
    getKeywordsArray(keywords) {
      if (typeof keywords === 'string') {
        try {
          return JSON.parse(keywords);
        } catch {
          return [keywords];
        }
      }
      return Array.isArray(keywords) ? keywords : [];
    },
    
    formatLastRun(dateString) {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);
      
      if (hours < 1) return 'Just now';
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    },
    
    formatNextRun(dateString) {
      if (!dateString) return 'Not scheduled';
      const date = new Date(dateString);
      const now = new Date();
      
      if (date <= now) return 'Due now';
      
      const diff = date - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);
      
      if (hours < 24) return `In ${hours} hour${hours > 1 ? 's' : ''}`;
      if (days < 7) return `In ${days} day${days > 1 ? 's' : ''}`;
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },
    
    formatScanTime(time) {
      if (!time) return '';
      if (time.includes(':')) {
        // Convert 24-hour to 12-hour format
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayHour = h % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
      }
      return time;
    },
    
    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    
    getLocationCount(gridSize) {
      const sizeMap = {
        '3x3': '9',
        '5x5': '25', 
        '7x7': '49',
        '10x10': '100'
      };
      return sizeMap[gridSize] || '25';
    },
    
    // Table actions
    async scanAgain(row) {
      // Check if we have the necessary ID
      const scanId = row.search_id || row.id;
      if (!scanId) {
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to rescan: No ID found'
        });
        return;
      }
      
      try {
        let response;
        let routeName;
        let routeParams;
        
        // Determine if this is a grid scan or quick scan
        if (row.scan_type === 'grid_scan') {
          // Call the grid scan rescan API
          response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/rescan`, {
            searchId: scanId
          }, {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          });
          
          if (response.data.success) {
            // Show success popup with detailed information
            await this.$swal({
              icon: 'success',
              title: 'Grid Scan Started Successfully!',
              confirmButtonText: 'OK',
              confirmButtonColor: '#4CAF50'
            });
            
            // Reload the page to show updated data
            window.location.reload();
          }
        } else {
          // Call the quick scan rescan API (existing logic)
          response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/rescan`, {
            scanId: row.id
          }, {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          });
          
          if (response.data.success) {
            const routeUrl = this.$router.resolve({
              name: 'QuickScanDetails',
              params: { id: response.data.search_id }
            });
            window.open(routeUrl.href, '_blank');
            window.location.reload();
          }
        }
        
        if (response && !response.data.success) {
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: response.data.message || 'Failed to start rescan'
          });
        }
      } catch (error) {
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to start rescan';
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        });
      }
    },
    
    viewDetails(row) {
      if (!row.search_id) {
          console.error('No search ID found for this row:', row);
          alert('Unable to view details: No ID found');
          return;
        }
        
        // Check scan type and route accordingly
        let routeConfig;
        
        if (row.scan_type === 'grid_scan') {
          // For grid scans, pass executionId as query parameter
          routeConfig = {
            name: 'GridScanDetails',
            params: { 
              id: row.search_id
            },
            query: row.execution_id ? { 
              execution: row.execution_id 
            } : {}
          };
        } else {
          // For quick scans
          routeConfig = {
            name: 'QuickScanDetails',
            params: { id: row.search_id }
          };
        }
        
        // Create the route URL
        const routeUrl = this.$router.resolve(routeConfig);
        
        // Open in new tab
        window.open(routeUrl.href, '_blank');
    },
    
    shareScan(row) {
      // Determine scan ID and type
      const scanId = row.search_id || row.id;
      const scanType = row.scan_type; // 'quick_scan' or 'grid_scan'
      
      if (!scanId || !scanType) {
        console.error('Unable to share: Missing scan ID or type', row);
        return;
      }
      
      this.shareScanId = scanId;
      this.shareScanType = scanType;
      this.showShareModal = true;
    },
    
    closeShareModal() {
      this.showShareModal = false;
      this.shareScanId = null;
      this.shareScanType = null;
    },
    
    onShareCopied(type) {
      const message = type === 'grid' ? 'Grid URL copied to clipboard!' : 
                      type === 'report' ? 'Report URL copied to clipboard!' : 
                      'Embed code copied to clipboard!';
      
      this.$swal({
        icon: 'success',
        title: 'Copied!',
        text: message,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    },
    
    toggleSelectAll() {
      if (this.isAllSelected) {
        // Deselect all items on current page
        this.paginatedData.forEach(row => {
          const index = this.selectedRows.indexOf(row.id);
          if (index > -1) {
            this.selectedRows.splice(index, 1);
          }
        });
      } else {
        // Select all items on current page
        this.paginatedData.forEach(row => {
          if (!this.selectedRows.includes(row.id)) {
            this.selectedRows.push(row.id);
          }
        });
      }
    },
    
    toggleRowSelect(id) {
      const index = this.selectedRows.indexOf(id);
      if (index > -1) {
        this.selectedRows.splice(index, 1);
      } else {
        this.selectedRows.push(id);
      }
    },
    
    isRowSelected(id) {
      return this.selectedRows.includes(id);
    },
    
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    
    handleClickOutside(event) {
      // Close actions dropdown if clicked outside
      if (!event.target.closest('.actions-dropdown-container')) {
        this.showActionsDropdown = false;
      }
    },
    
    async deleteSelectedScans() {
      if (this.selectedRows.length === 0) {
        alert('Please select at least one scan to delete');
        return;
      }
      
      const confirmMessage = this.selectedRows.length === 1 
        ? 'Are you sure you want to delete this scan?' 
        : `Are you sure you want to delete ${this.selectedRows.length} scans?`;
      
      const result = await this.$swal({
        title: 'Confirm Delete',
        text: confirmMessage,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel'
      });
      
      if (result.isConfirmed) {
        try {
          // Separate Quick Scans and Grid Scans
          const quickScanIds = [];
          const gridScanExecutionIds = [];
          
          this.selectedRows.forEach(rowId => {
            const row = this.tableData.find(r => r.id === rowId);
            if (row) {
              if (row.scan_type === 'quick_scan') {
                // For quick scans, use search_id
                quickScanIds.push(row.search_id);
              } else if (row.scan_type === 'grid_scan') {
                // For grid scans, pass execution_id
                gridScanExecutionIds.push(row.execution_id);
              }
            }
          });
          
          let response;
          let messages = [];
          
          // Handle Quick Scans deletion (existing logic)
          if (quickScanIds.length > 0) {
            response = await axios.delete(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete`, {
              data: { scanIds: quickScanIds },
              headers: {
                Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
                "Content-Type": "application/json"
              }
            });
            
            if (response.data.success) {
              messages.push(response.data.message);
            }
          }
          
          // Handle Grid Scans deletion (new endpoint)
          if (gridScanExecutionIds.length > 0) {
            response = await axios.delete(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete-grid-executions`, {
              data: { executionIds: gridScanExecutionIds },
              headers: {
                Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
                "Content-Type": "application/json"
              }
            });
            
            if (response.data.success) {
              messages.push(response.data.message);
            }
          }
          
          if (messages.length > 0) {
            this.$swal({
              icon: 'success',
              title: 'Deleted!',
              text: messages.join('. '),
              timer: 2000,
              showConfirmButton: false
            });
            
            // Clear selections and refresh data
            this.selectedRows = [];
            this.showActionsDropdown = false;
            await this.fetchScanExecutions();
          } else {
            throw new Error('No items were deleted');
          }
        } catch (error) {
          console.error('Error deleting scans:', error);
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to delete scans'
          });
        }
      }
    },
    
    // Pagination
    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.fetchScanExecutions(); // Fetch new page data
      }
    },
    
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.fetchScanExecutions(); // Fetch new page data
      }
    },
    
    // Modal backdrop close
    closeModalOnBackdrop(event, modalType) {
      if (event.target === event.currentTarget) {
        if (modalType === 'quickScan') {
          this.closeQuickScanModal();
        } else if (modalType === 'addGrid') {
          this.closeAddGridModal();
        } else if (modalType === 'gridsList') {
          this.closeGridsListModal();
        }
      }
    },


    // Renamed method to fetch scan executions instead of just quick scans
    async fetchScanExecutions() {
      this.isLoadingScans = true;
      this.scanError = null;
      
      try {
        // Calculate offset based on current page
        const offset = (this.currentPage - 1) * this.itemsPerPage;
        
        const response = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/scan-executions`, {
          params: {
            limit: this.itemsPerPage,
            offset: offset,
            search: this.searchQuery || ''
          },
          headers: {
            Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
            "Content-Type": "application/json"
          }
        });
        
        if (response.data.success) {
          // Transform API data to match table format - now includes both quick and grid scans
          this.tableData = response.data.data.map(scan => ({
            id: scan.id,
            search_id: scan.search_id,
            execution_id: scan.execution_id,
            scan_type: scan.type, // 'quick_scan' or 'grid_scan'
            gridName: scan.grid_name || scan.business_name,
            keywords: scan.keywords, // Keep as array for badge display
            businessName: scan.business_name,
            gridSize: scan.grid_size ? `${scan.grid_size}` : 'N/A',
            radius: scan.radius ? `${scan.radius} ${scan.distance_type === 'Kilometers' ? 'Kilometer' : 'Mile'}${scan.radius !== 1 ? 's' : ''}` : 'N/A',
            lastScan: this.formatDate(scan.execution_date),
            status: scan.status,
            progress: scan.progress
          }));
          
          // Update total count from backend
          if (response.data.pagination) {
            this.totalItems = response.data.pagination.total;
          }
        } else {
          this.scanError = 'Failed to fetch scan data';
          console.error('API returned success: false');
        }
      } catch (error) {
        this.scanError = 'Error fetching scans: ' + error.message;
        console.error('Error fetching scan executions:', error);
      } finally {
        this.isLoadingScans = false;
      }
    },
    
    // Legacy method for backward compatibility - redirects to new method
    async fetchQuickScans() {
      return this.fetchScanExecutions();
    },
    
    // Keep the helper methods the same:
    parseKeywords(keywordsString) {
      try {
        const keywords = JSON.parse(keywordsString);
        return Array.isArray(keywords) ? keywords.join(', ') : keywordsString;
      } catch (error) {
        return keywordsString; // Return as-is if not valid JSON
      }
    },
    
    getKeywordArray(keywords) {
      // If already an array, return it
      if (Array.isArray(keywords)) {
        return keywords;
      }
      
      // If it's a JSON string, parse it
      if (typeof keywords === 'string') {
        try {
          const parsed = JSON.parse(keywords);
          return Array.isArray(parsed) ? parsed : [keywords];
        } catch (error) {
          // If it's a comma-separated string, split it
          if (keywords.includes(',')) {
            return keywords.split(',').map(k => k.trim());
          }
          // Otherwise return as single-item array
          return [keywords];
        }
      }
      
      return [];
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    },
  },
  
  watch: {
    searchQuery: {
      handler() {
        // Reset to first page and refetch data when search query changes
        this.currentPage = 1;
        // Debounce search to avoid too many API calls
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
          this.fetchScanExecutions();
        }, 300);
      }
    }
  },
  
  mounted() {
    // Add click outside handler for actions dropdown
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);
  },
  
  async created() {
    // First load the API keys status
    await Promise.all([
      this.$store.dispatch('apiKeys/fetchGoogleMapsKey'),
      this.$store.dispatch('apiKeys/fetchValueSerpKey')
    ]);
    
    // Only load Google Maps API if key is valid
    if (this.isGoogleMapsValid) {
      try {
        await this.loadGoogleMapsAPI();
      } catch (error) {
        console.error('Failed to load Google Maps API:', error);
      }
    }
    
    // Fetch dashboard stats and quick scans
    await Promise.all([
      this.fetchDashboardStats(),
      this.fetchQuickScans()
    ]);
  },
  
  beforeUnmount() {
    // Clean up Google Maps resources
    if (this.quickScanMap) {
      this.clearQuickScanGrid();
    }
    if (this.addGridMap) {
      this.clearAddGridGrid();
    }
  }
}
</script>

<style scoped>
/* No Subscription Message */
.no-subscription-message {
  margin-top: 20px;
}

.no-subscription-message .bg-gray-50 {
  border: 2px dashed #e5e7eb;
}

/* Header Section */
.header-section {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0px 2px 20px 0px rgba(62, 187, 128, 0.1);
  border: 1px solid #E5E7EB;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.header-info h1.page-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #19233A;
  line-height: 38px;
  margin-bottom: 8px;
}

.page-description {
  color: #757B89;
  font-size: 16px;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.add-grid-btn {
  padding: 12px 20px;
  background: #FFFFFF;
  color: #374151;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.add-grid-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s ease;
}

.add-grid-btn:hover::before {
  left: 100%;
}

.add-grid-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.add-grid-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.btn-icon {
  font-size: 16px;
  line-height: 1;
}

/* Overview Widgets */
.overview-widgets {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.widget-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #f3f4f6;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
}

.widget-card:hover {
  transform: translateY(-2px);
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.1);
  background: #ffffff;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.widget-title {
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #757B89;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.widget-change {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 12px;
}

.widget-change.positive {
  background: #D1FAE5;
  color: #10b981;
}

.widget-change.negative {
  background: #FEE2E2;
  color: #ef4444;
}

.widget-value {
  font-family: 'Rubik', sans-serif;
  font-size: 2.25rem;
  font-weight: 600;
  color: #19233A;
  margin-bottom: 8px;
  line-height: 1;
}

.widget-subtitle {
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #757B89;
}

.widget-view-btn {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  background: rgba(79, 70, 229, 0.1);
  border: 1px solid rgba(79, 70, 229, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4f46e5;
  transition: all 0.2s ease;
}

.widget-view-btn:hover {
  background: rgba(79, 70, 229, 0.15);
  border-color: rgba(79, 70, 229, 0.3);
  transform: translateY(-1px);
}

.widget-view-btn svg {
  width: 16px;
  height: 16px;
}

/* Table Container */
.listing-table-container {
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 2px 20px 0px rgba(62, 187, 128, 0.1);
  padding: 40px;
}

.table-section-header {
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.table-section-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #19233A;
  line-height: 38px;
  margin-bottom: 8px;
}

.table-section-description {
  color: #757B89;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

/* Header Controls */
.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  gap: 20px;
}

.search-filter-group {
  display: flex;
  gap: 20px;
  align-items: center;
}

.search-bar {
  position: relative;
  width: 400px;
  height: 50px;
}

.search-bar input {
  width: 100%;
  height: 100%;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  padding: 13px 15px 13px 50px;
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  color: #19233A;
  background: #ffffff;
}

.search-bar input::placeholder {
  color: rgba(25, 35, 58, 0.4);
}

.search-bar .search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  opacity: 0.4;
}

.search-bar .search-icon svg {
  width: 100%;
  height: 100%;
  stroke: #19233A;
  fill: none;
}

.filter-button {
  width: 60px;
  height: 50px;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.filter-button svg {
  width: 30px;
  height: 30px;
  stroke: #757B89;
  fill: none;
}

.actions-dropdown-container {
  position: relative;
}

.actions-dropdown {
  width: 200px;
  height: 50px;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  background: #ffffff;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #19233A;
}

.actions-dropdown .arrow-down svg {
  width: 24px;
  height: 24px;
  stroke: #19233A;
  fill: none;
}

.actions-menu {
  position: absolute;
  top: 55px;
  right: 0;
  background: white;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 200px;
}

.action-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  font-size: 14px;
  color: #19233A;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;
}

.action-menu-item:hover:not(.disabled) {
  background-color: #f3f4f6;
}

.action-menu-item.delete {
  color: #ef4444;
}

.action-menu-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Table */
.table-container {
  margin-bottom: 40px;
  overflow-x: auto;
  width: 100%;
}

.table-header {
  height: 54px;
  background: #f8f9fc;
  border: 1px solid #E5E7EB;
  border-radius: 5px 5px 0 0;
  display: flex;
  align-items: center;
  padding: 0 20px;
  min-width: 1100px;
  box-sizing: border-box;
}

.table-row {
  min-height: 54px;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-top: none;
  display: flex;
  align-items: center;
  padding: 10px 20px;
  min-width: 1100px;
  box-sizing: border-box;
}

.table-row:hover {
  background-color: #f9fafb;
}

.table-row:last-child {
  border-radius: 0 0 5px 5px;
}

.table-cell {
  display: flex;
  align-items: center;
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  color: #19233A;
  padding: 0 5px;
  box-sizing: border-box;
}

.table-header .table-cell {
  font-weight: 500;
  color: #111929;
}

.checkbox-cell {
  width: 50px;
  flex-shrink: 0;
}

.scan-type-cell {
  width: 120px;
  flex-shrink: 0;
}

.scan-type-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.scan-type-label.grid {
  background: #f3f0ff;
  color: #7c3aed;
}

.scan-type-label.quick {
  background: #fef3c7;
  color: #d97706;
}

.type-icon {
  font-size: 14px;
}

.location-cell {
  width: 200px;
  flex-shrink: 0;
  padding-right: 10px;
}

.search-query-cell {
  width: 240px;
  flex-shrink: 0;
}

.keywords-container {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-start;
  width: 100%;
}

.keyword-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e0e7ff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  max-width: 95%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.business-cell {
  width: 180px;
}

.search-settings-cell {
  width: 180px;
  flex-shrink: 0;
  gap: 10px;
}

.last-scan-cell {
  width: 150px;
  flex-shrink: 0;
}

.action-cell {
  width: 120px;
  flex-shrink: 0;
  justify-content: center;
  gap: 8px;
  padding-right: 10px;
}

/* Checkbox */
.checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #757B89;
  border-radius: 2px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.checkbox.checked {
  background: #3ebb80;
  border-color: #3ebb80;
}

.checkbox.checked span {
  color: white;
  font-size: 12px;
  font-weight: bold;
}

/* Icons */
.grid-icon, .miles-icon {
  width: 18px;
  height: 18px;
  opacity: 0.6;
}

.grid-icon svg, .miles-icon svg {
  width: 100%;
  height: 100%;
  fill: #19233A;
}

.sort-icon {
  width: 24px;
  height: 24px;
  margin-left: 4px;
}

.sort-icon svg {
  width: 100%;
  height: 100%;
  fill: #757B89;
}

/* Modal Styles */
.modal {
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;  
  justify-content: flex-start; 
}

.modal-content {
  background: #FFFFFF;
  margin: 2% auto;
  padding: 32px;
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.grids-modal {
  max-width: 1000px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 26px !important;
  border-bottom: 1px solid #E5E7EB;
}

.modal-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  color: #19233A;
}

.close-modal {
  background: #f9fafb;
  border: none;
  color: #757B89;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.25rem;
  transition: background 0.2s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-modal:hover {
  background: #f3f4f6;
}

.modal-body {
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-family: 'Rubik', sans-serif;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #19233A;
  font-size: 0.875rem;
  font-family: 'Rubik', sans-serif;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-select {
  cursor: pointer;
}

.input-hint {
  font-size: 0.75rem;
  color: #757B89;
  margin-top: 4px;
  font-style: italic;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #E5E7EB;
}

.action-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn.primary {
  background: #4f46e5  !important;
  color: white  !important;
}

.action-btn.primary:hover {
  background: #4338ca !important;
}

.action-btn.secondary {
  background: #FFFFFF !important;
  color: #374151 !important;
  border: 1px solid #E5E7EB !important;
}

.action-btn.secondary:hover {
  background: #f9fafb !important;
}

.scan-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.scan-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

/* Grid Preview Map Styles */
.grid-preview-container {
  position: relative;
}

.grid-preview-map {
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
  position: relative;
}

.grid-info-badge {
  background: rgba(79, 70, 229, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: blur(8px);
}

.map-hint {
  font-size: 0.75rem;
  color: #757B89;
  margin-top: 6px;
  text-align: center;
  font-style: italic;
}

/* Scheduling Section */
.scheduling-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #E5E7EB;
}

.section-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  color: #19233A;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.checkbox-label:hover {
  color: #19233A;
}

.checkmark {
  font-size: 0.875rem;
}

/* Keyword Tags Styles */
.keywords-tag-container {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  padding: 8px;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
  position: relative;
}

.keywords-tag-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.keyword-tag-item {
  display: inline-flex;
  align-items: center;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 16px;
  padding: 6px 10px;
  font-size: 14px;
  color: #0369a1;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  max-width: 200px;
  animation: tagFadeIn 0.3s ease;
}

@keyframes tagFadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.keyword-tag-item:hover {
  background: #e0f2fe;
  border-color: #7dd3fc;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.keyword-tag-text {
  flex: 1;
  margin-right: 6px;
  cursor: text;
  outline: none;
  background: transparent;
  border: none;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  min-width: 50px;
  max-width: 150px;
}

.keyword-tag-text[contenteditable="true"]:focus {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  padding: 0 4px;
}

.keyword-tag-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.keyword-tag-delete:hover {
  background: #ef4444;
  transform: scale(1.1);
}

.keyword-tag-delete:hover svg {
  stroke: white;
}

.keyword-tag-delete svg {
  width: 12px;
  height: 12px;
  stroke: #ef4444;
  stroke-width: 2;
}

.keyword-tag-input {
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #19233A;
  background: transparent;
  padding: 6px 0;
}

.keyword-tag-input::placeholder {
  color: #9CA3AF;
  font-style: italic;
}

/* Competitor Tags */
.competitors-tag-container {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  padding: 8px;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
}

.competitors-tag-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.competitor-tag-item {
  display: inline-flex;
  align-items: center;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 16px;
  padding: 6px 10px;
  font-size: 14px;
  color: #dc2626;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  max-width: 300px;
  animation: tagFadeIn 0.3s ease;
}

.competitor-tag-item:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.competitor-tag-text {
  flex: 1;
  margin-right: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.competitor-tag-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.competitor-tag-delete:hover {
  background: #ef4444;
  transform: scale(1.1);
}

.competitor-tag-delete:hover svg {
  stroke: white;
}

.competitor-tag-delete svg {
  width: 12px;
  height: 12px;
  stroke: #ef4444;
  stroke-width: 2;
}

.competitor-search-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #19233A;
  font-size: 0.875rem;
  font-family: 'Rubik', sans-serif;
  transition: border-color 0.2s ease;
}

.competitor-search-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.competitor-search-input::placeholder {
  color: #9CA3AF;
}

/* Email Tags */
.email-tags-container {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  padding: 8px;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
}

.email-tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.email-tag-item {
  display: inline-flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 16px;
  padding: 6px 10px;
  font-size: 14px;
  color: #15803d;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;
  max-width: 300px;
  animation: tagFadeIn 0.3s ease;
}

.email-tag-item:hover {
  background: #dcfce7;
  border-color: #86efac;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.email-tag-item.invalid {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.email-tag-item.invalid:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.email-tag-text {
  flex: 1;
  margin-right: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.email-tag-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.email-tag-delete:hover {
  background: #ef4444;
  transform: scale(1.1);
}

.email-tag-delete:hover svg {
  stroke: white;
}

.email-tag-delete svg {
  width: 12px;
  height: 12px;
  stroke: #ef4444;
  stroke-width: 2;
}

.email-tag-input {
  flex: 1;
  min-width: 200px;
  border: none;
  outline: none;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #19233A;
  background: transparent;
  padding: 6px 0;
}

.email-tag-input::placeholder {
  color: #9CA3AF;
  font-style: italic;
}

/* Quick Scan Description */
.quick-scan-description {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.quick-scan-description p {
  margin: 0;
  color: #0369a1;
  font-size: 14px;
  line-height: 1.5;
}

/* Action Buttons */
.action-button {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: #2477F6;
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  text-transform: capitalize;
}

.action-button:hover {
  background: rgba(36, 119, 246, 0.1);
}

.action-button svg {
  width: 20px;
  height: 20px;
  fill: #2477F6;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #757B89;
}

.pagination-dropdown {
  width: 80px;
  height: 38px;
  border: 1px solid #c9c9d4;
  border-radius: 5px;
  background: #ffffff;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #757B89;
}

.pagination-button {
  width: 40px;
  height: 40px;
  border: 1px solid #c9c9d4;
  border-radius: 5px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.pagination-button:hover:not(.disabled) {
  background: #f8f9fc;
}

.pagination-button.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-button svg {
  width: 20px;
  height: 20px;
  fill: #757B89;
}

/* Grids Container */
.grids-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 24px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px;
}

.grid-card {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 32px;
  transition: all 0.2s ease;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.grid-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #4f46e5;
}

.grid-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.grid-card-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #19233A;
  line-height: 38px;
  margin-bottom: 4px;
}

.grid-card-subtitle {
  font-size: 14px;
  color: #757B89;
}

.grid-status-badge {
  background: #D1FAE5;
  color: #10b981;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.grid-status-badge.paused {
  background: #FEF3C7;
  color: #d97706;
}

.grid-settings {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.grid-setting-item {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f3f4f6;
  transition: all 0.2s ease;
}

.grid-setting-item:hover {
  background: #ffffff;
  border-color: #E5E7EB;
}

.grid-setting-label {
  font-size: 14px;
  color: #757B89;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.grid-setting-value {
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  color: #19233A;
  font-weight: 600;
  line-height: 1.2;
}

.grid-keywords {
  margin-bottom: 20px;
}

.grid-keywords-label {
  font-size: 14px;
  color: #757B89;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.grid-keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-tag {
  background: #f0f9ff;
  color: #0369a1;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #e0f2fe;
}

.competitor-tag {
  background: #fef2f2;
  color: #dc2626;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #fecaca;
}

.grid-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
}

.grid-action-btn {
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Rubik', sans-serif;
}

.grid-action-btn.scan {
  background: #FFFFFF;
  color: #10b981;
  border-color: #10b981;
}

.grid-action-btn.scan:hover {
  background: #f0fdf4;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.grid-action-btn.edit {
  background: #FFFFFF;
  color: #4f46e5;
  border-color: #4f46e5;
}

.grid-action-btn.edit:hover {
  background: #f8faff;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.grid-action-btn.delete {
  background: #FFFFFF;
  color: #ef4444;
  border-color: #ef4444;
}

.grid-action-btn.delete:hover {
  background: #fef2f2;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
.keyword-error-message {
  color: #dc2626; /* red-600 */
  font-size: 0.75rem;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}
/* Responsive Design */
@media (max-width: 1400px) {
  .container {
    padding: 15px;
  }
  
  .header-section {
    padding: 24px;
  }
  
  .overview-widgets {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  
  .widget-card {
    padding: 20px;
  }
  
  .listing-table-container {
    padding: 20px;
  }
  
  .search-bar {
    width: 300px;
  }
  
  .actions-dropdown {
    width: 150px;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }
  
  .header-info h1.page-title {
    font-size: 18px;
    line-height: 38px;
  }
  
  .overview-widgets {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .widget-card {
    padding: 16px;
  }
  
  .widget-value {
    font-size: 1.75rem;
  }
  
  .header-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-filter-group {
    justify-content: space-between;
  }
  
  .search-bar {
    width: 100%;
    max-width: 300px;
  }
  
  .table-header, .table-row {
    font-size: 12px;
    padding: 0 10px;
  }
  
  .location-cell, .search-query-cell, .business-cell {
    width: 120px;
  }
  
  .search-settings-cell {
    width: 100px;
  }
  
  .last-scan-cell {
    width: 140px;
  }

}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ffffff;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.action-btn.loading {
  opacity: 0.8;
  cursor: wait;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.action-btn.primary.loading span.spinner {
  margin-right: 8px;
}

/* Skeleton Loader Styles */
.skeleton-loader {
  position: relative;
  overflow: hidden;
  background-color: #f6f7f8;
  padding: 20px;
  border-radius: 12px;
  height: 140px;
}

.skeleton-header,
.skeleton-value,
.skeleton-subtitle {
  background: linear-gradient(
    90deg,
    rgba(207, 216, 220, 0.2) 25%,
    rgba(207, 216, 220, 0.3) 50%,
    rgba(207, 216, 220, 0.2) 75%
  );
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-header {
  height: 20px;
  width: 120px;
  margin-bottom: 16px;
}

.skeleton-value {
  height: 36px;
  width: 80px;
  margin-bottom: 12px;
}

.skeleton-subtitle {
  height: 16px;
  width: 100px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Widget neutral change style - hidden by default */
.widget-change.neutral {
  display: none;
}
</style>